/**
 * CALGARY-NW — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 51 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CALGARY_NW: Location[] = [
// ─────────────────────────────────────────────────────────── ARBOUR LAKE ──
  {
    slug: "arbour-lake",
    name: "Arbour Lake",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1314, lng: -114.2069 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "At about 14 km from Calgary International, Arbour Lake runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "In the last hour of the day, Arbour Lake outshines everything around it — the water takes the western sky's light while the rooftops along Arbour Lake Drive NW have already gone to shadow.",
      consequence:
        "Shot at midday, the same water reads flat and grey, and the community's one differentiator — the lake a buyer is paying to live beside — looks like any stormwater pond. Ground photography cannot show the lot's relationship to the water at all.",
      resolution:
        "A golden-hour hold timed to that last hour, with a boundary-line orbit that keeps the property and the lit water in the same frame, delivered in the standard turnaround.",
    },
    landmarks: ["Arbour Lake", "Crowfoot Crossing", "Crowfoot LRT", "Arbour Lake Drive NW"],
    terrain:
      "Rolling northwest bench terrain arranged around a constructed lake, with streets stepping toward the water and the Crowfoot commercial flats at the community's edge.",
    windNote:
      "Squarely in the chinook belt. A winter chinook can move surface wind from workable to grounded within the hour, so flight windows are planned around the gust spread rather than the forecast average.",
    seasonalWindow:
      "Golden hour runs late into summer evenings at this latitude and collapses to late afternoon in winter. The lake keeps a second calendar — open water between ice-off and freeze-up, then a white, featureless plate once it freezes.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day work is realistic when weather and authorisation align, and a trip here pairs naturally with Tuscany, Royal Oak or Rocky Ridge.",
    faqs: [
      {
        question: "Is Arbour Lake inside the Calgary airport control zone?",
        answer:
          "It sits on the edge. Arbour Lake is about 14 km from Calgary International, near the charted boundary of the CYYC control zone, so which side of the line an address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked.",
      },
      {
        question: "When should a lakefront property in Arbour Lake be photographed?",
        answer:
          "In the last hour of good light. At golden hour the lake takes the colour of the western sky and becomes the brightest element in the frame, which is exactly when an aerial should hold the house and the water together. At midday the same water reads flat and grey.",
      },
      {
        question: "Can an Arbour Lake shoot be booked same-day?",
        answer:
          "Often, yes. Arbour Lake is about 20 minutes from the Calgary base, so when weather and airspace align a same-day flight is realistic. It also pairs efficiently with Tuscany, Royal Oak or Rocky Ridge, so neighbouring properties can share one trip.",
      },
    ],
    primaryKeywords: [
      "drone photography Arbour Lake",
      "aerial photography Arbour Lake",
      "Arbour Lake drone services",
    ],
    nearestLocations: ["tuscany", "royal-oak", "rocky-ridge", "nw-calgary"],
  },

  // ─────────────────────────────────────────────────────────── BANFF TRAIL ──
  {
    slug: "banff-trail",
    name: "Banff Trail",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.077, lng: -114.117 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Banff Trail lies inside the charted CYYC control zone, about 9 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Foothills Medical Centre heliport operates about 2 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Chinook wind is Banff Trail's real schedule-setter: it arrives in gusts rather than averages, and across the open flats around McMahon Stadium a surge can move conditions from workable to grounded before a battery is spent.",
      consequence:
        "A shoot booked to a calendar instead of a wind window gets cancelled on arrival — or flies anyway and delivers footage with a wobble in the horizon that a client cannot unsee.",
      resolution:
        "Flight windows here are planned against the gust spread, with morning slots held through chinook season and the shoot re-timed inside the same day when the window moves — delivered in the standard turnaround.",
    },
    landmarks: [
      "McMahon Stadium",
      "SAIT Polytechnic",
      "University of Calgary",
      "Banff Trail LRT Station",
      "19 Street NW",
    ],
    terrain:
      "Flat inner-northwest grid between the university lands and the stadium flats, with modest fall to the southeast and open institutional ground breaking up the residential blocks.",
    windNote:
      "Squarely in the chinook belt. Winter west winds arrive in surges, and the gust spread — not the average — decides whether a low-altitude orbit is usable.",
    seasonalWindow:
      "Long summer evenings put golden light late into the day; winter compresses the window to a low afternoon sun. Chinook cycles strip snow quickly here, so mid-winter shoots often find bare ground rather than a white frame.",
    bylawNote:
      "Drone takeoff and landing in municipal parks and on pathways is restricted under City of Calgary bylaws; launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — close enough to re-time a shoot inside the same day when a wind window moves. Pairs readily with Capitol Hill, University Heights and Cambrian Heights.",
    faqs: [
      {
        question: "What does it take legally to fly a drone in Banff Trail?",
        answer:
          "Banff Trail is about 9 km from Calgary International, inside the CYYC Class C control zone. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, flight plans keep clear of the Foothills Medical Centre heliport approaches, and the site is checked against NAV Drone.",
      },
      {
        question: "Why are Banff Trail shoots scheduled against a wind window instead of a fixed hour?",
        answer:
          "Because chinook wind arrives in gusts, not averages. A west-wind surge can move conditions from workable to grounded within the hour, so the booking holds a window rather than a minute, with morning slots favoured in chinook season. The result is fewer cancellations and steadier footage.",
      },
      {
        question: "Can you photograph properties near McMahon Stadium and the University of Calgary?",
        answer:
          "Yes. Both sit inside the same control zone as the rest of Banff Trail, so the authorisation covers the specific site and altitude rather than the neighbourhood name. Frames that show a property's true distance to McMahon Stadium, SAIT Polytechnic or the University of Calgary are exactly what the aerial adds here.",
      },
    ],
    primaryKeywords: [
      "drone photography Banff Trail",
      "aerial photography Banff Trail",
      "Banff Trail drone services",
    ],
    nearestLocations: ["capitol-hill", "university-heights", "cambrian-heights"],
  },

  // ──────────────────────────────────────────────────── BEDDINGTON HEIGHTS ──
  {
    slug: "beddington-heights",
    name: "Beddington Heights",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1194, lng: -114.09 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "Beddington Heights sits about 5 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Beddington Heights sits about 5 km from Calgary International — deep enough inside the control zone that the question is never whether authorisation is needed, only what altitude it will be granted.",
      consequence:
        "An operator holding a Basic certificate cannot fly here at all, and one who files casually can be granted a ceiling too low for the shot list — the wide establishing frame from the edge of Nose Hill Park simply never gets made.",
      resolution:
        "The NAV CANADA authorisation is filed for the specific site and altitude before the date is booked, so the approved ceiling is known before the shot list is promised — and the work delivers in the standard turnaround.",
    },
    landmarks: [
      "Nose Hill Park",
      "Beddington Towne Centre",
      "Beddington Boulevard NW",
      "Beddington Heights School",
    ],
    terrain:
      "North-central benchland east of Nose Hill Park, mostly level along Beddington Boulevard NW, with a gentle fall away from the hill's flank toward the commercial ground at Beddington Towne Centre.",
    windNote:
      "Chinook belt. Winter west winds arrive in surges, and the gust spread rather than the hourly average sets the flying limit.",
    seasonalWindow:
      "The open grass of Nose Hill Park carries the seasons for the wide frame — green early, gold by late summer, brown after frost — while chinook cycles strip mid-winter snow from the streets faster than from the shaded lots.",
    bylawNote:
      "Municipal parks and pathways are off-limits for drone takeoff and landing under City of Calgary bylaws, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Huntington Hills, Thorncliffe and MacEwan Glen sit immediately adjacent, so one trip can serve several addresses — each with its own airspace check.",
    faqs: [
      {
        question: "How close is Beddington Heights to the airport, and what does that mean for drones?",
        answer:
          "About 5 km — close enough that the community sits well inside the CYYC Class C control zone. Every flight runs under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, and the specific site and altitude are checked against NAV Drone first.",
      },
      {
        question: "What altitude will a Beddington Heights shoot be allowed?",
        answer:
          "Whatever NAV CANADA grants for the specific site — this close to the airport, the approved ceiling depends on where the address sits relative to the CYYC approach paths. Because the authorisation is filed before the date is booked, the working ceiling is known before the shot list is promised.",
      },
      {
        question: "Can one trip cover Beddington Heights and its neighbours?",
        answer:
          "Yes. Beddington Heights is about 20 minutes from the Calgary base, and Huntington Hills, Thorncliffe and MacEwan Glen sit immediately around it, so several properties can share a single mobilisation. Each site still gets its own airspace check, because the approved altitude can change street by street here.",
      },
    ],
    primaryKeywords: [
      "drone photography Beddington Heights",
      "aerial photography Beddington Heights",
      "Beddington Heights drone services",
    ],
    nearestLocations: ["huntington-hills", "thorncliffe", "macewan-glen"],
  },

  // ─────────────────────────────────────────────────────────────── BOWNESS ──
  {
    slug: "bowness",
    name: "Bowness",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.087, lng: -114.172 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "At about 12 km from Calgary International, Bowness runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The charted edge of the CYYC control zone runs close to Bowness, and the community strings along the Bow River — so two addresses on Bow Crescent NW can sit on opposite sides of the line that decides the paperwork.",
      consequence:
        "A quote written from the community name rather than the address gets the paperwork wrong in one direction or the other — either a filing that was never needed, or a booked date that cannot legally be flown.",
      resolution:
        "Every Bowness address is checked against NAV Drone before the date is booked; where the site falls inside the zone, the authorisation is filed as part of booking, and the shoot delivers in the standard turnaround.",
    },
    landmarks: [
      "Bowness Park",
      "Bow River",
      "Bow Crescent NW",
      "Bowness Road NW",
      "Shouldice Athletic Park",
    ],
    terrain:
      "Valley-floor community on a bend of the Bow River, with the water along one side, treed flats through the middle and the valley slope rising behind the community.",
    windNote:
      "Chinook belt, with the Bow valley adding its own behaviour — west wind follows the river corridor, so conditions on the flats can run windier than the citywide forecast suggests.",
    seasonalWindow:
      "The Bow holds stretches of open water through much of the winter, giving mid-winter aerials a live element when the rest of the frame is white. Spring runoff changes the river's width and colour; summer evenings lay low gold light along the corridor.",
    bylawNote:
      "Bowness Park and the other municipal parks fall under City of Calgary bylaws that restrict drone takeoff and landing, so launches are staged from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Montgomery, Valley Ridge and Silver Springs are minutes away, so river-corridor properties can be grouped into a single trip.",
    faqs: [
      {
        question: "Do drone flights in Bowness need airport authorisation?",
        answer:
          "It depends on the exact address. Bowness sits about 12 km from Calgary International, near the charted edge of the CYYC control zone, so the line can fall on either side of a given property. Each site is checked against NAV Drone, and where authorisation is required it is filed before booking.",
      },
      {
        question: "Can you launch a drone in Bowness Park?",
        answer:
          "No — City of Calgary bylaws restrict drone takeoff and landing in municipal parks, and Bowness Park is municipal land. Launches are staged from private property with the owner's consent, and frames that include the park or the Bow River are composed from a compliant position within the approved airspace.",
      },
      {
        question: "What does an aerial add for a Bowness property?",
        answer:
          "The river. Bowness strings along a bend of the Bow, and a home's relationship to the water — backing onto Bow Crescent NW, a short walk from Bowness Park — is the detail buyers pay for. Ground photography cannot show that geometry; a boundary-line orbit settles it in one frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Bowness",
      "aerial photography Bowness",
      "Bowness drone services",
    ],
    nearestLocations: ["montgomery", "valley-ridge", "silver-springs"],
  },

  // ───────────────────────────────────────────────────────────── BRENTWOOD ──
  {
    slug: "brentwood",
    name: "Brentwood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.087, lng: -114.137 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Brentwood lies inside the charted CYYC control zone, about 9 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Alberta Children's Hospital heliport operates about 1 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Wind in Brentwood is not one number: on a gusty chinook afternoon the air downwind of Northland Village Mall's big rooftops runs rougher than the air over the residential streets, and the airframe feels the difference before anyone on the ground does.",
      consequence:
        "A low orbit over a commercial subject, planned to the same limits as a house shoot, comes back with shaky frames — and the re-fly costs the client the light they originally booked.",
      resolution:
        "Commercial work here is scheduled into calmer morning air where the forecast allows, and the flight plan carries altitude margin over rooftop turbulence — delivered in the standard turnaround.",
    },
    landmarks: [
      "Northland Village Mall",
      "Nose Hill Park",
      "Brentwood LRT Station",
      "University of Calgary",
    ],
    terrain:
      "Established northwest plateau between Nose Hill Park and the university lands — mostly level residential blocks under mature trees, with open commercial flats around Northland Village Mall.",
    windNote:
      "Chinook belt. Gusty west flow produces rough, mechanical air behind large buildings, so commercial and residential subjects here fly to different wind limits.",
    seasonalWindow:
      "Full summer leaf hides much of the housing from oblique angles, so roof and lot detail photographs best between leaf-fall and green-up. Chinook cycles strip winter snow quickly; a clean white frame here is a narrow target.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and along pathways, so launch sites are private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Charleswood, Collingwood and Varsity adjoin it, so multiple sites in this pocket are planned into one trip.",
    faqs: [
      {
        question: "What approvals does a Brentwood drone shoot need?",
        answer:
          "Brentwood is about 9 km from Calgary International, inside the CYYC Class C control zone, so flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed. Flight plans also keep clear of the Alberta Children's Hospital heliport approaches, and every site is checked against NAV Drone.",
      },
      {
        question: "Why is wind handled differently for Brentwood's commercial sites?",
        answer:
          "Large rooftops shed rough, turbulent air downwind on gusty days, so the air over the Northland Village Mall flats behaves differently than the air above a quiet residential street. Commercial orbits here are flown with altitude margin and scheduled into calmer morning windows when a chinook is running.",
      },
      {
        question: "Which nearby communities pair with a Brentwood booking?",
        answer:
          "Charleswood, Collingwood and Varsity sit directly around Brentwood, and the base is about 15 minutes away, so one trip can cover several addresses. Because this pocket shares the same control zone and the same hospital heliport consideration, the airspace work for neighbouring sites is planned together before the drive.",
      },
    ],
    primaryKeywords: [
      "drone photography Brentwood",
      "aerial photography Brentwood",
      "Brentwood drone services",
    ],
    nearestLocations: ["charleswood", "collingwood", "varsity"],
  },

  // ────────────────────────────────────────────────────── CAMBRIAN HEIGHTS ──
  {
    slug: "cambrian-heights",
    name: "Cambrian Heights",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.087, lng: -114.102 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Cambrian Heights lies inside the charted CYYC control zone, about 7 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Foothills Medical Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Cambrian Heights keeps its evening light longer than the park below it — as the sun drops, Confederation Park's ravine goes to shadow first while the rooflines along the rim above stay lit.",
      consequence:
        "Timed wrong, half the frame is already dead — the park that sells the location reads as a dark trench under lit houses, or the houses go grey while the grass below still glows.",
      resolution:
        "A golden-hour hold planned against the ravine's shadow line rather than the published sunset keeps the park and the rooftops in the same usable light, delivered in the standard turnaround.",
    },
    landmarks: ["Confederation Park", "Nose Hill Park", "Cambrian Drive NW", "SAIT Polytechnic"],
    terrain:
      "High ground along the north rim of Confederation Park's ravine, with level streets on top and the green cut of the park falling away below the community's edge.",
    windNote:
      "Chinook belt. West flow arrives in gusts, and the flying limit is read from the gust spread rather than the average; calm at the door does not mean calm at altitude.",
    seasonalWindow:
      "The ravine sets the clock: its floor loses direct light before the rooftops do, so evening windows are planned to the shadow line rather than the published sunset. The park's trees carry autumn colour that reads strongly from above; chinooks strip the snow fast.",
    bylawNote:
      "Under City of Calgary bylaws, municipal parks — Confederation Park included — are not available for drone takeoff or landing; launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Capitol Hill, Collingwood and Rosemont sit alongside, so an evening light window can cover more than one address in a single trip.",
    faqs: [
      {
        question: "Is Cambrian Heights controlled airspace for drone photography?",
        answer:
          "Yes. The community sits about 7 km from Calgary International, inside the CYYC Class C control zone, so flights run under an Advanced RPAS certificate with NAV CANADA authorisation obtained before the shoot is confirmed. Plans keep clear of the Foothills Medical Centre heliport approaches, and NAV Drone confirms each site.",
      },
      {
        question: "What is the best hour for aerial photography in Cambrian Heights?",
        answer:
          "The last stretch of the evening, timed to terrain rather than the almanac. Confederation Park's ravine falls into shadow before the rooftops above it, so the shoot is scheduled to the shadow line — early enough that park and houses share the light, late enough that the light is worth having.",
      },
      {
        question: "Can an aerial show a home's position above Confederation Park?",
        answer:
          "That is the frame this community is built for. A boundary-line orbit along the ravine edge holds the house and the green cut of the park in one image — a relationship no street-level photograph can carry, because the park falls away below the sightline of a camera on a tripod.",
      },
    ],
    primaryKeywords: [
      "drone photography Cambrian Heights",
      "aerial photography Cambrian Heights",
      "Cambrian Heights drone services",
    ],
    nearestLocations: ["capitol-hill", "collingwood", "rosemont"],
  },

  // ────────────────────────────────────────────────────────── CAPITOL HILL ──
  {
    slug: "capitol-hill",
    name: "Capitol Hill",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.08, lng: -114.105 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "Capitol Hill sits about 8 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Foothills Medical Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The only water in a Capitol Hill frame is the thin course through Confederation Park, and from the air it reads as a dark seam through the green — a feature nobody notices from 16 Avenue NW.",
      consequence:
        "A ground-level photographer cannot use it at all, and a careless aerial loses it in the trees — the one soft element in an otherwise hard grid goes missing from the frame.",
      resolution:
        "Frames along the park edge are composed to keep the water line and the subject in the same pass — the difference between a photo of a grid and a photo with somewhere for the eye to rest — delivered in the standard turnaround.",
    },
    landmarks: ["Confederation Park", "SAIT Polytechnic", "16 Avenue NW", "Banff Trail LRT Station"],
    terrain:
      "Inner-northwest grid rising gently northward, bounded by the institutional ground of SAIT Polytechnic to the south and the green cut of Confederation Park to the north.",
    windNote:
      "Chinook belt. Winter west winds run in gusts rather than steady flow, and the gust spread decides whether a low orbit over the grid is usable on a given afternoon.",
    seasonalWindow:
      "Grass along Confederation Park greens before the street trees leaf out, and the park's colour turns first again in fall. Long summer evenings drive low light down the east–west avenues; midwinter compresses the day to a brief, low-angle window.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in municipal parks and on pathways, so flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Banff Trail, Cambrian Heights and Mount Pleasant border it, so inner-northwest addresses are readily grouped into a single trip, each site with its own airspace check.",
    faqs: [
      {
        question: "Does a Capitol Hill drone shoot need NAV CANADA approval?",
        answer:
          "Yes. Capitol Hill is about 8 km from Calgary International, inside the CYYC Class C control zone, so every flight runs under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the date is confirmed. Flight plans keep clear of the Foothills Medical Centre heliport approaches nearby.",
      },
      {
        question: "What does aerial photography show in Capitol Hill that street photos cannot?",
        answer:
          "Position. From altitude, one frame can hold a property, the green corridor of Confederation Park and the campus edge of SAIT Polytechnic — the walkable geometry that drives value here. Street photography shows a facade; the aerial shows what the address is actually near, at honest distances.",
      },
      {
        question: "Are rental and infill properties near SAIT worth aerial coverage?",
        answer:
          "Yes — this is where it earns its keep. For rentals, a single aerial proves the distance to SAIT Polytechnic and the Banff Trail LRT Station better than any paragraph. For infill projects, a progress series from the same positions documents the build honestly from excavation to completion.",
      },
    ],
    primaryKeywords: [
      "drone photography Capitol Hill",
      "aerial photography Capitol Hill",
      "Capitol Hill drone services",
    ],
    nearestLocations: ["banff-trail", "cambrian-heights", "mount-pleasant"],
  },

  // ──────────────────────────────────────────────────────────── CARRINGTON ──
  {
    slug: "carrington",
    name: "Carrington",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.177, lng: -114.118 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Carrington sits about 9 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Carrington is still building, and the seasons grade it: between snowmelt and green-up the community shows every phase at once from the air — finished streets, framed blocks and bare-graded earth along Carrington Boulevard NW, each a different colour.",
      consequence:
        "That mixed frame cuts both ways for a builder — spring mud beside finished show homes reads as honest progress in a dated series, and as unfinished mess in a one-off marketing shot.",
      resolution:
        "Progress work here is flown as a repeating series so each phase lands in the same frame visit after visit, and stand-alone marketing frames are timed after green-up — both delivered in the standard turnaround.",
    },
    landmarks: [
      "Carrington Boulevard NW",
      "Vivo Recreation Centre",
      "Carrington Park",
      "Stoney Trail access",
    ],
    terrain:
      "Open prairie on the city's northern edge grading into new subdivision blocks — level to gently rolling ground where construction phases run from bare earth to finished streetscape.",
    windNote:
      "Chinook belt. On the open northern edge there is little terrain to modify the regional forecast, so what the forecast promises is usually what arrives — in gusts, when a chinook is running.",
    seasonalWindow:
      "Snow lingers on graded, shadowed lots after the finished streets are clear, and green-up reaches new sod noticeably later than established grass. The honest construction record is a series flown across the year; the flattering marketing frame is high summer.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and on public pathways, so launches are staged from private property — on active sites, from within the builder's own ground.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — practical for recurring progress visits. Symons Valley, Sherwood and Kincora sit on the same run north, so multiple sites can share the drive.",
    faqs: [
      {
        question: "Does drone work in Carrington require airport authorisation?",
        answer:
          "Yes — Carrington sits about 9 km from Calgary International, inside the CYYC Class C control zone as charted. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, and the specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "How does construction progress photography work in Carrington?",
        answer:
          "On a repeating grid. Each visit flies the same positions and headings over the site, so the series reads as one build advancing rather than a pile of unrelated photos. At about 25 minutes from the base, recurring visits are practical to schedule around pours, framing milestones and possession dates.",
      },
    ],
    primaryKeywords: [
      "drone photography Carrington",
      "aerial photography Carrington",
      "Carrington drone services",
    ],
    nearestLocations: ["symons-valley", "sherwood", "kincora"],
  },

  // ─────────────────────────────────────────────────────────── CHARLESWOOD ──
  {
    slug: "charleswood",
    name: "Charleswood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.095, lng: -114.13 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Charleswood falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 2 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "A Charleswood flight plan answers to two maps at once: the CYYC control zone that covers the community, and the Alberta Children's Hospital heliport whose approaches pass close by — neither visible from the ground, both deciding where a drone may be.",
      consequence:
        "An operator who clears the airport paperwork but ignores the heliport can hold an authorisation that still does not cover the shot — a plan that looks legal on one chart fails on the other.",
      resolution:
        "Both constraints go into one flight plan before booking: the NAV CANADA authorisation is filed for the specific site and the route is drawn clear of the heliport approaches, with the shoot delivered in the standard turnaround.",
    },
    landmarks: [
      "Nose Hill Park",
      "Charleswood Drive NW",
      "Brentwood LRT Station",
      "Confederation Park",
    ],
    terrain:
      "Curving mid-century streets on the rising ground below Nose Hill Park, with steady tree cover through the blocks and the open grass of the hill immediately north.",
    windNote:
      "Chinook belt. Gusts run stronger over the open grass of Nose Hill Park than between the treed blocks below it, and the flying limit is read from the gust spread aloft.",
    seasonalWindow:
      "In full leaf the street trees close over much of Charleswood, so roofline and lot detail photograph best from leaf-fall through green-up. Nose Hill Park's grass gives the wide frame its calendar — green early, gold by late summer.",
    bylawNote:
      "Municipal parkland — Nose Hill Park included — is closed to drone takeoff and landing under City of Calgary bylaws, so launches come from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Brentwood, Collingwood and University Heights adjoin it, so one trip commonly serves several addresses in this pocket of the northwest.",
    faqs: [
      {
        question: "What airspace rules apply to drone flights in Charleswood?",
        answer:
          "Charleswood sits about 9 km from Calgary International, inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and a NAV CANADA authorisation obtained before the shoot is confirmed. Flight plans also keep clear of the Alberta Children's Hospital heliport approaches, and NAV Drone confirms each site.",
      },
      {
        question: "Does the hospital heliport stop drone shoots in Charleswood?",
        answer:
          "No — it shapes them. The Alberta Children's Hospital heliport operates nearby, so routes and altitudes are drawn clear of its approaches as part of the same plan that carries the airport authorisation. The shoot still happens; it simply happens along a line both charts approve.",
      },
      {
        question: "Can an aerial show how close a Charleswood home is to Nose Hill Park?",
        answer:
          "Yes, and it is often the frame that matters most. A boundary-line orbit holds the property and the open grass of Nose Hill Park in a single image, proving an adjacency that street photography can only assert. For homes on the park side of Charleswood Drive NW, that one frame carries the listing.",
      },
    ],
    primaryKeywords: [
      "drone photography Charleswood",
      "aerial photography Charleswood",
      "Charleswood drone services",
    ],
    nearestLocations: ["brentwood", "collingwood", "university-heights"],
  },

  // ─────────────────────────────────────────────────────────────── CITADEL ──
  {
    slug: "citadel",
    name: "Citadel",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1252, lng: -114.1962 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "Citadel sits about 13 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Citadel's street names — Citadel Ridge Drive NW, Citadel Peak Bay NW — describe the terrain honestly: high, open ground on the city's northwest shoulder that takes a west wind at full strength.",
      consequence:
        "Flight windows close earlier here than the citywide forecast implies. A crew that books to the downtown wind number can arrive to find the gust spread over the ridge already past the airframe's limit.",
      resolution:
        "Citadel bookings carry an on-site wind check and a fallback window in the same trip, with the flight plan favouring the sheltered side of the subject when the gusts run — delivered in the standard turnaround.",
    },
    landmarks: [
      "Citadel Ridge Drive NW",
      "Citadel Peak Bay NW",
      "Citadel Pond",
      "Citadel Park",
      "Nose Hill Park",
    ],
    terrain:
      "High rolling ground in the far northwest, with streets following the ridge lines and stormwater ponds — Citadel Pond among them — set into the low points between them.",
    windNote:
      "Chinook belt at full exposure: elevated, open terrain where winter west winds arrive unmoderated. The gust spread, not the average, governs the flying day.",
    seasonalWindow:
      "Snow holds longer on this high ground and its north-facing slopes than in lower communities, and chinook melts arrive in stripes — bare south faces beside white shadow lines. Long summer evenings put low gold light along the ridge lines.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and on pathways, so Citadel launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Ranchlands, Edgemont and Hamptons are on the same run, so a single trip can cover several far-northwest addresses.",
    faqs: [
      {
        question: "Does Citadel fall inside the Calgary airport control zone?",
        answer:
          "It sits near the line. Citadel is about 13 km from Calgary International, close to the charted boundary of the CYYC control zone, so the answer can change from one address to the next. Each site is planned as controlled until NAV Drone shows otherwise, with any authorisation filed before booking.",
      },
      {
        question: "Why does Citadel get an on-site wind check before flying?",
        answer:
          "Because the community stands on high, open ground that takes a west wind at full strength — a chinook's gusts here can run past the airframe's limit while the city forecast still reads workable. Checking on site, with a fallback window in the same trip, protects the booking and the footage.",
      },
    ],
    primaryKeywords: [
      "drone photography Citadel",
      "aerial photography Citadel",
      "Citadel drone services",
    ],
    nearestLocations: ["ranchlands", "edgemont", "hamptons"],
  },

  // ─────────────────────────────────────────────────────────── COLLINGWOOD ──
  {
    slug: "collingwood",
    name: "Collingwood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1, lng: -114.118 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "About 8 km from Calgary International, Collingwood falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 3 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From above, Collingwood reads nothing like the avenues around it: the streets curve — Collingwood Drive NW, Chelsea Road NW — and wide lots sit under a tree canopy mature enough to hide whole rooflines in summer.",
      consequence:
        "That canopy defeats ground photography twice — it hides the house from the street, and it hides the lot's true size from every angle a buyer can reach on foot.",
      resolution:
        "A pass above the canopy line puts the roof, the lot boundary and the curve of the street into one honest frame, opened with a rising reveal for the wide shot — delivered in the standard turnaround.",
    },
    landmarks: ["Collingwood Drive NW", "Chelsea Road NW", "Confederation Park", "Nose Hill Park"],
    terrain:
      "Gently sloping ground between Confederation Park and the lower flank of Nose Hill Park, laid out in curving mid-century streets under mature tree cover.",
    windNote:
      "Chinook belt. Above the canopy the airframe flies in the full west wind no matter how calm the street feels below, so the window is read from gusts aloft, not conditions at the door.",
    seasonalWindow:
      "The canopy sets the calendar: full leaf hides rooflines and lot edges, so structural detail photographs best between leaf-fall and green-up, while autumn colour along the curving streets makes the community's strongest wide frame.",
    bylawNote:
      "Confederation Park and other municipal parkland are closed to drone takeoff and landing under City of Calgary bylaws; launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Charleswood, Brentwood and Cambrian Heights surround it, so one trip routinely covers several addresses in the inner northwest.",
    faqs: [
      {
        question: "Is drone photography permitted in Collingwood?",
        answer:
          "Yes, with the right paperwork. Collingwood sits about 8 km from Calgary International, inside the CYYC Class C control zone, so flights run under an Advanced RPAS certificate with NAV CANADA authorisation obtained before the shoot is confirmed. Plans keep clear of the Alberta Children's Hospital heliport approaches nearby.",
      },
      {
        question: "When should a Collingwood home be shot from the air?",
        answer:
          "It depends on what the frame must prove. Roofline, lot size and the curve of the street photograph best between leaf-fall and green-up, when the mature canopy is open. The green wide shot — the one that sells the neighbourhood itself — belongs to full summer leaf and autumn colour.",
      },
      {
        question: "What does an aerial reveal about a Collingwood lot?",
        answer:
          "Its actual size and shape. Collingwood's wide, deep lots sit under tree cover that hides them from every angle available on foot, so buyers routinely underestimate what is there. One pass above the canopy line shows the roof, the boundary and the curving street in a single honest frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Collingwood",
      "aerial photography Collingwood",
      "Collingwood drone services",
    ],
    nearestLocations: ["charleswood", "brentwood", "cambrian-heights"],
  },

  // ───────────────────────────────────────────────────────── COUNTRY HILLS ──
  {
    slug: "country-hills",
    name: "Country Hills",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1497, lng: -114.1005 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 7 km from Calgary International, Country Hills falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Country Hills is laid out around its golf course, and from the air the arrangement is the message: the fairways of the Country Hills Golf Club thread between the residential blocks, and the premium lots are the ones whose back fences meet the green.",
      consequence:
        "'Backs onto the golf course' is the claim that sells here, and it is exactly the claim a ground-level photo cannot prove — the fence line hides the fairway from every camera position on the street.",
      resolution:
        "A boundary-line orbit along the fence line holds the lot and the fairway in the same frame and settles the claim in a single image, delivered in the standard turnaround.",
    },
    landmarks: [
      "Country Hills Golf Club",
      "Nose Creek Park",
      "Country Hills Boulevard NW",
      "Country Hills Village",
    ],
    terrain:
      "Rolling ground shaped around golf terrain and the Nose Creek drainage, with residential blocks following the contours rather than a grid and the course threading between them.",
    windNote:
      "Chinook belt. Winter west flow arrives in gusts, and the open golf terrain gives an honest read of what the airframe will meet aloft before it launches.",
    seasonalWindow:
      "The golf course drives the calendar: the strongest frames run from spring green-up until the fairways lose their colour in fall, when they hold green against the rooftops. In winter the course reads as white negative space and the built form carries the frame.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing on municipal park land and pathways; launches are staged from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Panorama Hills, Country Hills Village and Harvest Hills adjoin it, so north-Calgary bookings are commonly grouped into a single trip.",
    faqs: [
      {
        question: "What are the drone rules for Country Hills?",
        answer:
          "Country Hills is about 7 km from Calgary International, inside the CYYC Class C control zone as charted, so flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can you prove a Country Hills home backs onto the golf course?",
        answer:
          "In one frame, yes. A boundary-line orbit along the fence puts the lot and the fairway of the Country Hills Golf Club into the same image, which settles the claim the way no street photo can, because the fence blocks the view from the ground.",
      },
    ],
    primaryKeywords: [
      "drone photography Country Hills",
      "aerial photography Country Hills",
      "Country Hills drone services",
    ],
    nearestLocations: ["panorama-hills", "country-hills-village", "harvest-hills"],
  },
// ──────────────────────────────────────────────── COUNTRY HILLS VILLAGE ──
  {
    slug: "country-hills-village",
    name: "Country Hills Village",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1523, lng: -114.085 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 6,
      authorizationRequired: true,
      authorizationNote:
        "Country Hills Village lies inside the charted CYYC control zone, about 6 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Water organises Country Hills Village — Country Village Road NE runs a closed loop with stormwater ponds at its centre, and the Nose Creek Pathway carries the drainage along the community's edge.",
      consequence:
        "From the sidewalk none of that water makes the frame. A listing or a lease photo shows parking and façade, while the ponds that give the address its character sit just out of view.",
      resolution:
        "A boundary-line orbit flown over the loop puts the water, the retail and the property in one frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Country Hills Village Shopping Centre",
      "Vivo Recreation Centre",
      "Nose Creek Pathway",
      "Country Village Road NE",
    ],
    terrain:
      "Gently sloping ground falling east toward the Nose Creek drainage, built as a compact ring of retail and multi-family housing around central ponds.",
    windNote:
      "Chinook belt, like all of north Calgary. The lower ground along the Nose Creek Pathway takes some shelter, but the west wind's gust spread still sets the flight window.",
    seasonalWindow:
      "Open water reads best from ice-off to freeze-up; once the ponds go white the frame leans on low golden-hour light and clean snow geometry instead.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["commercial-retail", "luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Same-day scheduling is realistic, and a booking pairs naturally with Country Hills, Harvest Hills or Coventry Hills next door.",
    faqs: [
      {
        question: "Is drone photography allowed in Country Hills Village this close to the airport?",
        answer:
          "Yes, with authorisation. Country Hills Village sits about 6 km from Calgary International, inside the CYYC Class C control zone. Fly4MEdia flies here under an Advanced RPAS certificate, files the NAV CANADA authorisation before your date is confirmed, and checks the exact site and altitude against NAV Drone before every flight.",
      },
      {
        question: "Can an aerial photo show my business inside Country Hills Village Shopping Centre?",
        answer:
          "Yes. The centre wraps around water, and an orbit shows a unit's position relative to the ponds, the parking and Country Village Road NE in a single frame — context a storefront photo cannot carry. That framing works for leasing material as well as for completed listings.",
      },
    ],
    primaryKeywords: [
      "drone photography Country Hills Village",
      "aerial photography Country Hills Village",
      "Country Hills Village drone services",
    ],
    nearestLocations: ["country-hills", "harvest-hills", "coventry-hills"],
  },

  // ──────────────────────────────────────────────────────── COVENTRY HILLS ──
  {
    slug: "coventry-hills",
    name: "Coventry Hills",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1447, lng: -114.073 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "Coventry Hills sits about 5 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Evening light leaves Coventry Hills from the bottom up — the low ground around Nose Creek Park goes to shadow while the rooflines along Coventry Boulevard NE are still lit.",
      consequence:
        "Shoot at the wrong hour and the frame splits in two: half warm roofline, half cold shadow, and neither the house nor its setting reads cleanly.",
      resolution:
        "A golden-hour hold timed to the last even light across the community keeps house and backdrop in the same exposure, delivered in the standard turnaround.",
    },
    landmarks: [
      "Vivo Recreation Centre",
      "Nose Creek Park",
      "Coventry Hills School",
      "Coventry Boulevard NE",
    ],
    terrain:
      "Rolling north-Calgary ground that falls away eastward to the Nose Creek valley, laid out in looping crescents of family housing.",
    windNote:
      "Chinook belt. West wind arrives over open ground with little to break it, and the Nose Creek valley to the east adds a local channel on strong days.",
    seasonalWindow:
      "Long summer evenings stretch the golden hour late; in winter the low southern sun means the creek side of the community loses direct light early.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks and on public pathways is restricted under the parks bylaws, so flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base. An easy same-day booking, and one visit can cover Harvest Hills, Country Hills or Panorama Hills in the same window.",
    faqs: [
      {
        question: "Do drone flights over Coventry Hills need NAV CANADA approval?",
        answer:
          "Yes. Coventry Hills lies about 5 km from Calgary International inside the CYYC Class C control zone, so every flight needs a NAV CANADA RPAS authorisation flown under an Advanced certificate. Fly4MEdia files that authorisation before confirming your date and verifies the site and altitude in NAV Drone first.",
      },
      {
        question: "What time of day photographs Coventry Hills best?",
        answer:
          "Late evening in summer. The community slopes toward Nose Creek Park, and the low ground goes to shadow before the rooftops do, so the last even light is the window that holds both. We schedule Coventry Hills shoots against that light rather than against a clock time.",
      },
    ],
    primaryKeywords: [
      "drone photography Coventry Hills",
      "aerial photography Coventry Hills",
      "Coventry Hills drone services",
    ],
    nearestLocations: ["harvest-hills", "country-hills", "panorama-hills"],
  },

  // ───────────────────────────────────────────────────────────── CRESTMONT ──
  {
    slug: "crestmont",
    name: "Crestmont",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0903, lng: -114.2547 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 17,
      authorizationRequired: true,
      authorizationNote:
        "Crestmont is about 17 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Crestmont stands on the high ground above the Paskapoo Slopes at Calgary's western gate, and a chinook reaches its rooflines before it reaches almost anywhere else in the city.",
      consequence:
        "A forecast written for the city centre can be wrong here by the time the crew arrives — gusts that ground a flight at Crestmont may not even register across the rest of the city.",
      resolution:
        "Crestmont shoots are planned around the wind, with a golden-hour hold flown in the evening lull when the west wind typically drops, delivered in the standard turnaround.",
    },
    landmarks: [
      "Calaway Park",
      "Canada Olympic Park",
      "Paskapoo Slopes",
      "Crestmont Boulevard SW",
    ],
    terrain:
      "Elevated ground at the city's western edge, above the Paskapoo Slopes, with the land stepping down east toward Canada Olympic Park and opening west to unbuilt country.",
    windNote:
      "Full chinook exposure. The community faces the foothills with no shelter, so west wind arrives at strength and the gust spread is the number that decides a flight.",
    seasonalWindow:
      "West-facing height of land, so evening light is the working light; chinooks strip snow off this exposed ground faster than sheltered valley communities keep it.",
    bylawNote:
      "City of Calgary parks bylaws restrict launching and landing in municipal parks and on pathways; Crestmont flights launch from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "construction-progress"],
    mobilisationNote:
      "About 20 minutes west of the base. Same-day work is practical, and the location pairs well with Springbank, Cochrane or Tuscany bookings.",
    faqs: [
      {
        question: "Which airport controls the airspace over Crestmont?",
        answer:
          "Calgary International (CYYC). Crestmont sits about 17 km out, near the charted boundary of the CYYC control zone, so the exact address decides the paperwork. Fly4MEdia plans Crestmont as controlled airspace until NAV Drone shows otherwise for the specific site, and files any required authorisation before the date is booked.",
      },
      {
        question: "Does Crestmont's wind cancel drone shoots often?",
        answer:
          "Less often than the exposure suggests, because the schedule is built around it. Crestmont takes the west wind first, so shoots are placed in the calmer evening window and booked with room to move a day if a chinook arrives. Wind delays a Crestmont shoot; it rarely defeats one.",
      },
    ],
    primaryKeywords: [
      "drone photography Crestmont",
      "aerial photography Crestmont",
      "Crestmont drone services",
    ],
    nearestLocations: ["rockland-park", "cochrane", "springbank", "tuscany"],
  },

  // ───────────────────────────────────────────────────────────── DALHOUSIE ──
  {
    slug: "dalhousie",
    name: "Dalhousie",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.101, lng: -114.159 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Dalhousie lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Alberta Children's Hospital heliport operates about 3 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Dalhousie answers to two pieces of airspace at once — the CYYC control zone measured at about 10 km, and the Alberta Children's Hospital heliport operating about 3 km away.",
      consequence:
        "An operator who plans against the airport alone can put a flight path across a hospital helicopter approach without knowing it, and that is the kind of error that ends a shoot on site.",
      resolution:
        "Both constraints are plotted before booking: the NAV CANADA authorisation covers the control zone, the flight plan keeps clear of the heliport approaches, and the work is delivered in the standard turnaround.",
    },
    landmarks: [
      "Dalhousie Station",
      "Market Mall",
      "Nose Hill Park",
      "Dalhousie Community Centre",
    ],
    terrain:
      "A mature plateau community on the shoulder of the northwest hills, with Nose Hill Park rising to the east and the ground falling south toward the valley communities.",
    windNote:
      "Chinook belt. West wind crosses the community's open upper streets at strength, and Nose Hill to the east does nothing to slow a westerly.",
    seasonalWindow:
      "Mature tree canopy means the community reads greenest in full summer; in winter, low sun and bare trees open up sightlines that the canopy hides in July.",
    bylawNote:
      "Launching and landing in City of Calgary parks and on pathways is restricted by parks bylaws, so Dalhousie flights are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Same-day scheduling is realistic, and Hawkwood, Silver Springs or Varsity can be shot on the same trip.",
    faqs: [
      {
        question: "What airspace rules apply to a drone shoot in Dalhousie?",
        answer:
          "Dalhousie is inside the Calgary International Class C control zone, about 10 km from the airport, with the Alberta Children's Hospital heliport roughly 3 km away. Flights run under an Advanced RPAS certificate with NAV CANADA authorisation filed before the date is confirmed, and clear of the heliport approaches.",
      },
      {
        question: "Does the hospital heliport stop drone photography in Dalhousie?",
        answer:
          "No — it shapes the flight plan rather than preventing it. The heliport's approach paths are treated as keep-out lines, and the shoot is flown so the aircraft never needs to cross them. Most Dalhousie properties can be photographed fully while honouring that separation.",
      },
    ],
    primaryKeywords: [
      "drone photography Dalhousie",
      "aerial photography Dalhousie",
      "Dalhousie drone services",
    ],
    nearestLocations: ["hawkwood", "silver-springs", "varsity"],
  },

  // ────────────────────────────────────────────────────────────── EDGEMONT ──
  {
    slug: "edgemont",
    name: "Edgemont",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1197, lng: -114.1503 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Edgemont sits about 10 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 5 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Edgemont is laid out around the Edgemont Ravines — the streets bend to follow the green cuts instead of crossing them, so from above the community reads as ridges of roofline separated by ravine.",
      consequence:
        "Ground photography sees only the streetscape. A walkout backing onto ravine looks like any other house from the kerb, and its best feature never enters the frame.",
      resolution:
        "A rising reveal from ravine grade to roofline shows the drop the home is built against, delivered in the standard turnaround.",
    },
    landmarks: [
      "Edgemont Ravines",
      "Nose Hill Park",
      "Edgemont Community Centre",
      "Edgevalley Way NW",
    ],
    terrain:
      "High rolling ground on the northwest shoulder of the city, cut by ravines that drain toward lower communities, with Nose Hill Park rising across the southeast edge.",
    windNote:
      "Chinook belt, and elevated with it. The ravines give individual properties pockets of shelter, but the community's upper streets take the west wind unbroken.",
    seasonalWindow:
      "Snow lingers in the shaded ravine bottoms after the rooflines are clear, which gives late-winter frames strong graphic contrast; green-up runs the other way, bottoms first.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in parks, ravines and pathway corridors, so Edgemont launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base — an easy same-day booking, often paired with Hamptons or Hidden Valley on the same trip.",
    faqs: [
      {
        question: "Does a drone shoot in Edgemont need special authorisation?",
        answer:
          "Yes. Edgemont sits about 10 km from Calgary International inside the CYYC Class C control zone, and the Alberta Children's Hospital heliport operates about 5 km away. Fly4MEdia flies under an Advanced RPAS certificate, files NAV CANADA authorisation before confirming the date, and keeps clear of the heliport approaches.",
      },
      {
        question: "Why do Edgemont walkout homes benefit from aerial photography?",
        answer:
          "Because the ravines are the point. Edgemont's housing is built along green cuts, and a walkout's position above ravine is invisible from the street. An aerial frame restores that relationship — house, drop and green corridor in one image — which is usually the property's strongest selling argument.",
      },
    ],
    primaryKeywords: [
      "drone photography Edgemont",
      "aerial photography Edgemont",
      "Edgemont drone services",
    ],
    nearestLocations: ["hamptons", "hidden-valley", "ranchlands"],
  },

  // ────────────────────────────────────────────────────────────── EVANSTON ──
  {
    slug: "evanston",
    name: "Evanston",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.175, lng: -114.1444 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Evanston is about 11 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From altitude, Evanston reads as a set of residential loops hung off Evanston Drive NW, with its commerce — Sage Hill Crossing and Creekside Shopping — pushed out to the community's edges.",
      consequence:
        "That geometry is exactly what a buyer or tenant wants to understand — how far the house actually sits from the shops and the exits — and no ground photograph can answer it.",
      resolution:
        "An approach run flown along the community's spine ties a specific address to its shopping and its way out of the neighbourhood in one continuous shot, delivered in the standard turnaround.",
    },
    landmarks: [
      "Symons Valley Ranch",
      "Sage Hill Crossing",
      "Creekside Shopping",
      "Evanston Drive NW",
    ],
    terrain:
      "Young rolling ground on Calgary's north rim, crescents and parks stepped over low hills with open country still visible north of the community.",
    windNote:
      "Chinook belt with true northern exposure — Evanston fronts open prairie to the north, so wind arrives with little in its way from either quarter.",
    seasonalWindow:
      "New streets and young trees mean the light does the work here; low spring and autumn sun raking across the rooflines gives the strongest frames, and snow shows street geometry cleanly.",
    bylawNote:
      "Under City of Calgary parks bylaws, drones do not take off or land in municipal parks or on pathways; Evanston launches are made from private property with the client's consent.",
    subjectMix: [
      "new-build-subdivision",
      "construction-progress",
      "luxury-residential",
      "commercial-retail",
    ],
    mobilisationNote:
      "About 25 minutes from the base. Comfortably same-day, and a single trip can also serve Sage Hill, Nolan Hill, Carrington or Livingston.",
    faqs: [
      {
        question: "Is Evanston inside the Calgary airport control zone?",
        answer:
          "It sits right at the question's edge — about 11 km out, near the charted CYYC boundary, where the exact address decides the paperwork. Fly4MEdia plans Evanston as controlled airspace until NAV Drone shows otherwise for the specific site, and files any required authorisation before booking the date.",
      },
      {
        question: "Can a builder get repeat progress photography in Evanston?",
        answer:
          "Yes, and the community suits it. Evanston is still building, and progress passes flown from the same coordinates and altitude on each visit produce a like-for-like series that reads as one build story. At about 25 minutes from base, repeat visits schedule easily around weather and site milestones.",
      },
    ],
    primaryKeywords: [
      "drone photography Evanston",
      "aerial photography Evanston",
      "Evanston drone services",
    ],
    nearestLocations: ["sage-hill", "nolan-hill", "carrington", "livingston"],
  },

  // ────────────────────────────────────────────────────────────── HAMPTONS ──
  {
    slug: "hamptons",
    name: "Hamptons",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1349, lng: -114.1493 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "About 10 km from Calgary International, Hamptons falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The fairways of Hamptons Golf Club are the community's open ground, and low evening sun raking along them gives the green corridors a modelling that midday light erases entirely.",
      consequence:
        "A fairway-backing home shot at noon loses the very contours the course gives it — the frame shows flat green instead of the shaped ground a buyer is paying a premium for.",
      resolution:
        "A golden-hour hold over the fairway line catches the long shadows that give the course its shape, with the home placed against it, delivered in the standard turnaround.",
    },
    landmarks: [
      "Hamptons Golf Club",
      "Nose Hill Park",
      "Beacon Hill Shopping Centre",
      "Hamptons Way NW",
    ],
    terrain:
      "Rolling upland threaded by golf fairways, with housing arranged along the course edges and the ground falling gently away toward Nose Hill Park to the southeast.",
    windNote:
      "Chinook belt on elevated northwest ground. The open fairway corridors leave long stretches without windbreak, so launch points are chosen for shelter.",
    seasonalWindow:
      "The course sets the calendar: fairways green early, hold colour into autumn, and read as clean white geometry after snow — three distinct looks for the same address across a year.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so Hamptons launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base, an easy same-day slot, and neighbouring Edgemont, Hidden Valley and MacEwan Glen can share the trip.",
    faqs: [
      {
        question: "Can a drone legally photograph a home in the Hamptons?",
        answer:
          "Yes, with the right paperwork. Hamptons lies about 10 km from Calgary International, inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and NAV CANADA authorisation. Fly4MEdia files that authorisation before your date is confirmed and verifies the site in NAV Drone beforehand.",
      },
      {
        question: "When should a golf-course-backing Hamptons home be photographed?",
        answer:
          "In the last hour of good light. Low sun runs along the Hamptons Golf Club fairways and models the shaped ground behind the home; midday light flattens it. Timing the shoot to that window is the difference between a green backdrop and a course the buyer can actually see.",
      },
    ],
    primaryKeywords: [
      "drone photography Hamptons",
      "aerial photography Hamptons",
      "Hamptons drone services",
    ],
    nearestLocations: ["edgemont", "hidden-valley", "macewan-glen"],
  },

  // ───────────────────────────────────────────────────────── HARVEST HILLS ──
  {
    slug: "harvest-hills",
    name: "Harvest Hills",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1385, lng: -114.067 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Harvest Hills lies inside the charted CYYC control zone, about 4 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Winter in Harvest Hills is not one season but several — chinooks strip and repaint the snow across this open ground repeatedly, so the same street off Harvest Hills Boulevard NE can read as deep winter one week and bare autumn the next.",
      consequence:
        "A listing shot in the brown-and-white patchwork between chinooks looks accidental — neither season, no contrast — and the client rarely knows that was the problem.",
      resolution:
        "Shoots are timed against the chinook cycle rather than the calendar: either fresh full cover or clean bare ground, whichever serves the property, delivered in the standard turnaround.",
    },
    landmarks: [
      "Harvest Hills Golf Club",
      "Vivo Recreation Centre",
      "Harvest Hills Shopping Centre",
      "Harvest Hills Boulevard NE",
    ],
    terrain:
      "Open, gently rolling north-Calgary ground organised around the green corridors of Harvest Hills Golf Club, with long sightlines in every direction.",
    windNote:
      "Chinook belt, fully exposed. There is no meaningful terrain shelter this far up the north slope, so the gust spread — not the average — decides the flight window.",
    seasonalWindow:
      "Between chinooks the snow cover is a coin toss all winter; the reliable frames are high-summer green and the fresh-snow mornings before the next warm wind arrives.",
    bylawNote:
      "Takeoff and landing in City of Calgary parks and pathway corridors is restricted by parks bylaws, so Harvest Hills flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 20 minutes from the base — realistic same-day, and Coventry Hills, Country Hills and Huntington Hills sit close enough to share the booking.",
    faqs: [
      {
        question: "How does being 4 km from the airport affect a Harvest Hills drone shoot?",
        answer:
          "It decides the paperwork, not the possibility. Harvest Hills sits about 4 km from Calgary International, inside the CYYC Class C control zone, so every flight runs under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the date is confirmed, and the site is checked in NAV Drone first.",
      },
      {
        question: "What is the best season to photograph a Harvest Hills property?",
        answer:
          "Whichever season is actually present that week. Chinooks turn north-Calgary winters into an alternation of snow cover and bare ground, so the honest answer is timing: full summer green is dependable, and in winter the shoot is booked to land on fresh cover or clean bare ground, not the patchwork between.",
      },
    ],
    primaryKeywords: [
      "drone photography Harvest Hills",
      "aerial photography Harvest Hills",
      "Harvest Hills drone services",
    ],
    nearestLocations: ["coventry-hills", "country-hills", "huntington-hills"],
  },

  // ────────────────────────────────────────────────────────────── HAWKWOOD ──
  {
    slug: "hawkwood",
    name: "Hawkwood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.118, lng: -114.178 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "At about 12 km from Calgary International, Hawkwood runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The 'brow' in Hawksbrow Road NW is a surveyor's honesty — Hawkwood runs along a height of land, and its upper streets record wind that the communities below never feel.",
      consequence:
        "A citywide forecast that reads flyable can be wrong at Hawkwood roofline height, and an operator who discovers that on arrival burns the client's booking for nothing.",
      resolution:
        "Wind here is planned for, not discovered: shoots are set into the day's calmer windows — usually a golden-hour hold at the end of it — with a fallback slot agreed at booking, delivered in the standard turnaround.",
    },
    landmarks: [
      "Nose Hill Park",
      "Hawkwood Park",
      "Hawkwood School",
      "Hawksbrow Road NW",
    ],
    terrain:
      "A ridge-line community on the city's northwest height of land, its streets tracing the brow while the ground falls away below and Nose Hill Park stands across the gap to the east.",
    windNote:
      "Chinook belt at elevation. The west wind meets Hawkwood's brow unbroken, and exposed upper streets routinely carry more wind than the sheltered ground below.",
    seasonalWindow:
      "High, open and west-facing, so the community keeps late evening light well; wind, not light, is what usually sets the shooting window here.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone launches and landings in parks including Hawkwood Park and Nose Hill Park, so takeoff is staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base. Same-day is realistic, with Silver Springs, Scenic Acres and Dalhousie natural companions on one trip.",
    faqs: [
      {
        question: "Does Hawkwood fall inside controlled airspace?",
        answer:
          "Effectively at the boundary. Hawkwood is about 12 km from Calgary International, near the charted edge of the CYYC control zone, so which side of the line an address falls on decides the paperwork. Fly4MEdia treats it as controlled until NAV Drone confirms otherwise and files authorisation before booking.",
      },
      {
        question: "Is Hawkwood windier than the rest of Calgary?",
        answer:
          "Its upper streets often are. The community sits along a height of land, and the brow takes the west wind without shelter, so roofline conditions can exceed the citywide forecast. Shoots are placed in the day's calmer windows, with a fallback slot agreed when the date is booked.",
      },
    ],
    primaryKeywords: [
      "drone photography Hawkwood",
      "aerial photography Hawkwood",
      "Hawkwood drone services",
    ],
    nearestLocations: ["silver-springs", "scenic-acres", "dalhousie"],
  },

  // ───────────────────────────────────────────────────────── HIDDEN VALLEY ──
  {
    slug: "hidden-valley",
    name: "Hidden Valley",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.145, lng: -114.133 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Hidden Valley falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Hidden Valley's street names file a report on the ground — Hidden Creek Rise NW and Hidden Spring Way NW mark the drainage that shapes the community, and its water gathers where the land does, in Hidden Valley Park.",
      consequence:
        "None of that reads from the kerb. A home near the park photographs like any suburban street, when its actual position beside water and green space is the thing worth showing.",
      resolution:
        "A boundary-line orbit around the property places the park and its water in the same frame as the house, delivered in the standard turnaround.",
    },
    landmarks: [
      "Hidden Valley Park",
      "Nose Hill Park",
      "Hidden Valley School",
      "Hidden Creek Rise NW",
      "Hidden Spring Way NW",
    ],
    terrain:
      "A shallow bowl of family streets draining toward a central park, ringed by higher ground, with Nose Hill Park standing to the south beyond the neighbouring communities.",
    windNote:
      "Chinook belt. The community's lower streets take some shelter from the surrounding higher ground, but the rim streets meet the west wind directly.",
    seasonalWindow:
      "The park water reads from ice-off to freeze-up; after freeze-up the frame shifts to snow-lined street geometry and the low winter sun.",
    bylawNote:
      "Parks bylaws restrict drone takeoff and landing on City of Calgary park land, Hidden Valley Park included, so flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base and easily same-day; Hamptons, MacEwan Glen and Panorama Hills pair naturally on the same run.",
    faqs: [
      {
        question: "What authorisation does a drone flight in Hidden Valley need?",
        answer:
          "A NAV CANADA RPAS authorisation, flown under an Advanced certificate. Hidden Valley sits about 9 km from Calgary International inside the CYYC Class C control zone, so Fly4MEdia files the authorisation before your shoot date is confirmed and verifies the exact site and altitude against NAV Drone before flying.",
      },
      {
        question: "Can aerial photos show how close a Hidden Valley home is to the park?",
        answer:
          "Yes — that relationship is the reason to go aerial here. An orbit frames the house, Hidden Valley Park and its water together, so a buyer sees the actual distance rather than reading it off a map. Ground photography cannot capture that adjacency at all.",
      },
    ],
    primaryKeywords: [
      "drone photography Hidden Valley",
      "aerial photography Hidden Valley",
      "Hidden Valley drone services",
    ],
    nearestLocations: ["hamptons", "macewan-glen", "panorama-hills"],
  },

  // ────────────────────────────────────────────────────────────── HIGHWOOD ──
  {
    slug: "highwood",
    name: "Highwood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.108, lng: -114.062 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Highwood lies inside the charted CYYC control zone, about 4 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Highwood sits where Calgary's street plan loosens — south of it the inner-city avenues hold a straight grid, and its own streets, Highwood Drive NW among them, begin to bend — a transition one aerial frame can show whole.",
      consequence:
        "For an infill or a listing here, position is the argument: minutes to the centre, green space at Confederation Park and the Nose Creek Pathway close by — and none of that context survives a ground-level photograph.",
      resolution:
        "An approach run toward the property carries that context into the frame — the bend of the streets, the green edges, the proximity — delivered in the standard turnaround.",
    },
    landmarks: [
      "Confederation Park",
      "Nose Creek Pathway",
      "Highwood School",
      "Highwood Drive NW",
    ],
    terrain:
      "Inner-north tableland between two green corridors, with Confederation Park cutting a treed hollow along one edge and the ground stepping down east toward the Nose Creek Pathway.",
    windNote:
      "Chinook belt like the rest of the city; the Nose Creek valley east of the community channels wind along its length, while the tableland above takes the westerlies directly.",
    seasonalWindow:
      "Mature canopy country — high summer hides rooflines under trees and photographs green; the bare-tree shoulder seasons open the sightlines and suit structural and infill work best.",
    bylawNote:
      "City of Calgary parks bylaws restrict launching and landing in parks such as Confederation Park and along pathways, so Highwood flights are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base — the shortest run in this corridor, easily same-day, and Thorncliffe, Huntington Hills or Mount Pleasant can join the same booking.",
    faqs: [
      {
        question: "Can you fly a drone in Highwood this close to Calgary International?",
        answer:
          "Yes, under authorisation. Highwood sits about 4 km from the airport, well inside the CYYC Class C control zone, so every flight runs under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the date is confirmed. The exact site and altitude are verified in NAV Drone before flying.",
      },
      {
        question: "How quickly can a Highwood drone shoot be booked?",
        answer:
          "Highwood is about 15 minutes from the Fly4MEdia base, the shortest drive in this corridor, so same-day scheduling is realistic once the airspace authorisation is in hand. The usual constraint on a Highwood date is weather — wind or ceiling — rather than travel or paperwork.",
      },
    ],
    primaryKeywords: [
      "drone photography Highwood",
      "aerial photography Highwood",
      "Highwood drone services",
    ],
    nearestLocations: ["thorncliffe", "huntington-hills", "mount-pleasant"],
  },

  // ────────────────────────────────────────────────────── HUNTINGTON HILLS ──
  {
    slug: "huntington-hills",
    name: "Huntington Hills",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1153, lng: -114.07 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Huntington Hills lies inside the charted CYYC control zone, about 4 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Huntington Hills ends in a drop — along its eastern edge the ground falls away to Nose Creek Park, and the community's level interior gives no hint of it until the last street.",
      consequence:
        "An east-side listing photographed from the street looks landlocked suburbia; the valley outlook that sets those addresses apart never appears in the frame.",
      resolution:
        "A rising reveal flown from park grade up past the roofline shows the drop and the outlook in one continuous move, delivered in the standard turnaround.",
    },
    landmarks: [
      "Nose Creek Park",
      "Huntington Hills Community Centre",
      "Huntington Hills Arena",
      "Huntington Road NE",
    ],
    terrain:
      "A level plateau of mature family streets that breaks off sharply on its east side, where the land steps down into the Nose Creek valley.",
    windNote:
      "Chinook belt. The plateau takes the west wind across open rooflines, while the Nose Creek valley below channels its own flow along the community's east flank.",
    seasonalWindow:
      "Snow and shadow hold longest on the valley slopes below the plateau; up top, chinooks clear the streets fast, so winter frames work best right after fresh cover.",
    bylawNote:
      "Drone takeoff and landing in City of Calgary parks — Nose Creek Park included — is restricted by parks bylaws, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base and straightforward same-day; Thorncliffe, Beddington Heights and Highwood sit close enough to share one trip.",
    faqs: [
      {
        question: "Is it legal to fly a drone in Huntington Hills so near the airport?",
        answer:
          "Yes, with authorisation in place. Huntington Hills is about 4 km from Calgary International, inside the CYYC Class C control zone, so flights are conducted under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed. Site and altitude are checked in NAV Drone first.",
      },
      {
        question: "Do east-side Huntington Hills homes photograph differently?",
        answer:
          "Yes. The streets backing the Nose Creek valley sit on an edge the rest of the community does not have, and their outlook over the park is invisible from the kerb. A climb from valley grade to roofline is usually the frame that sells those addresses.",
      },
    ],
    primaryKeywords: [
      "drone photography Huntington Hills",
      "aerial photography Huntington Hills",
      "Huntington Hills drone services",
    ],
    nearestLocations: ["thorncliffe", "beddington-heights", "highwood"],
  },
// ─────────────────────────────────────────────────────────────── KINCORA ──
  {
    slug: "kincora",
    name: "Kincora",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.162, lng: -114.165 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "At about 11 km from Calgary International, Kincora runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From above, Kincora resolves into a single system of loops — Kincora Drive NW curling into Kincora Rise NW and Kincora Bay NW — with almost no straight line in the plan except the commercial edge at Beacon Hill Shopping Centre.",
      consequence:
        "A ground-level photograph cannot orient a buyer inside that geometry — a house on a curving court could be anywhere, and its position relative to Kincora Park or the shopping centre is exactly what the listing needs to prove.",
      resolution:
        "One elevated hold above the property places the lot inside the loop plan and shows its relationship to Kincora Park and Beacon Hill Shopping Centre, delivered in the standard turnaround.",
    },
    landmarks: [
      "Kincora Drive NW",
      "Kincora Park",
      "Beacon Hill Shopping Centre",
      "Symons Valley Ranch",
    ],
    terrain:
      "Rolling upland on the city's northwest rim, the subdivision bending around green space at Kincora Park, with the land easing down toward Symons Valley Ranch.",
    windNote:
      "Chinook belt, on the city's exposed northwest shoulder. West wind arrives over open country with little to slow it, and the gust spread matters more than the average on the forecast.",
    seasonalWindow:
      "Snow arrives early and lingers on this high ground; green-up runs later than in the valley communities. Long June evenings put low light across the loops when the plan photographs best.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Comfortably a same-day booking when weather and authorisation align, and easily paired with Sherwood, Symons Valley or Panorama Hills in one trip.",
    faqs: [
      {
        question: "Does a Kincora drone shoot need NAV CANADA authorisation?",
        answer:
          "Often, yes. Kincora sits about 11 km from Calgary International, near the charted boundary of the CYYC control zone, so which side of the line an address falls on decides the paperwork. Fly4MEdia plans it as controlled airspace until NAV Drone shows otherwise, and files any authorisation before the date is booked.",
      },
      {
        question: "Why do aerial photos suit Kincora listings so well?",
        answer:
          "The street plan is built from loops and courts rather than a grid, so a buyer cannot easily place Kincora Rise NW or Kincora Bay NW on a mental map. One elevated frame orients the lot inside the loops and shows its position relative to Beacon Hill Shopping Centre and Kincora Park.",
      },
      {
        question: "Can Kincora be combined with nearby communities in one booking?",
        answer:
          "Yes. Kincora is about 25 minutes from the Calgary base, and Sherwood, Symons Valley and Panorama Hills sit in the same corridor. A single mobilisation can cover shoots in two or three of these communities inside one light window, which is the economical way to book smaller jobs.",
      },
    ],
    primaryKeywords: [
      "drone photography Kincora",
      "aerial photography Kincora",
      "Kincora drone services",
    ],
    nearestLocations: ["sherwood", "symons-valley", "panorama-hills"],
  },

  // ──────────────────────────────────────────────────────────── LIVINGSTON ──
  {
    slug: "livingston",
    name: "Livingston",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.2069, lng: -114.0972 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Livingston sits about 11 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Livingston is being built on some of the flattest ground in the city's northwest — from the air, Centre Street N runs toward open farmland with barely a contour line to interrupt it.",
      consequence:
        "Flat ground gives a ground-level camera nothing to work with — every elevation shot of a new build looks like the one beside it, and a construction site reads as the same mud from one visit to the next.",
      resolution:
        "Altitude supplies the relief the land withholds: a repeatable progress series from a fixed elevated station makes phase-over-phase change legible, and a rising reveal over Livingston Hub gives a finished listing its geography — in the standard turnaround.",
    },
    landmarks: [
      "Livingston Boulevard NW",
      "Centre Street N",
      "Livingston Hub",
      "Carrington Plaza",
    ],
    terrain:
      "Flat to gently rolling prairie at the city's northern limit; new streets spread along Centre Street N with open farmland beginning immediately beyond the build edge.",
    windNote:
      "Chinook belt. The community's open northern edge faces farmland with no shelter, so wind the inner city reads as moderate can arrive here at full strength.",
    seasonalWindow:
      "On the open northern edge, snow drifts rather than settles and green-up comes late to the surrounding farmland. The build-out sets its own rhythm — construction frames read best between spring thaw and freeze-up.",
    bylawNote:
      "Calgary's parks bylaws do not permit drone launching or landing in municipal parks; takeoff is planned from private property with the owner's permission.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base along the Centre Street N corridor. Same-day work is realistic, and Carrington, Evanston, Sage Hill or Nolan Hill can share the mobilisation.",
    faqs: [
      {
        question: "Is Livingston inside the Calgary airport control zone?",
        answer:
          "It sits on the fence line. Livingston is about 11 km from Calgary International, near the charted edge of the CYYC control zone, so the answer can change from one address to the next. Fly4MEdia treats it as controlled until NAV Drone shows otherwise and files authorisation before the booking is confirmed.",
      },
      {
        question: "How do you document construction progress in Livingston?",
        answer:
          "With a repeatable frame. The same elevated station, heading and altitude are flown on each visit, so a builder can lay one month against the next and see the change instead of guessing at it. On ground this flat, that consistency is what makes a progress series legible.",
      },
      {
        question: "When is the best light for aerial work in Livingston?",
        answer:
          "Early and late. With open farmland immediately north, the sun reaches Livingston unobstructed at both ends of the day, and low light gives the flat ground the shadow relief it lacks at noon. Midday frames read flat here, so bookings favour the first and last hours of light.",
      },
    ],
    primaryKeywords: [
      "drone photography Livingston",
      "aerial photography Livingston",
      "Livingston drone services",
    ],
    nearestLocations: ["carrington", "evanston", "sage-hill", "nolan-hill"],
  },

  // ────────────────────────────────────────────────────────── MACEWAN GLEN ──
  {
    slug: "macewan-glen",
    name: "MacEwan Glen",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1424, lng: -114.1497 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "MacEwan Glen lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "MacEwan Glen ends in a hard line: its rooflines stop at the edge of Nose Hill Park, and from the air the boundary between street and grassland is as abrupt as a shoreline.",
      consequence:
        "For a home backing the park, that adjacency is most of the price — and it is invisible from the kerb. A street photo shows a fence; it cannot prove there is open grassland on the other side of it.",
      resolution:
        "A boundary-line orbit along the park edge sets the property against the grassland and proves the adjacency in a single frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "MacEwan Glen Drive NW",
      "MacEwan Glen Park",
      "Nose Hill Park",
      "Beddington Trail",
    ],
    terrain:
      "Rolling ground off the north shoulder of Nose Hill, the community sloping gently away from the park's high grassland toward Beddington Trail.",
    windNote:
      "Chinook belt. The community sits on open rolling ground beside Nose Hill's grassland, and west wind crosses it without much to break the flow.",
    seasonalWindow:
      "Nose Hill's grassland holds its tawny colour long after the streets green up in spring, and in autumn the community's canopy turns against it. Low evening light gives the boundary its clearest read.",
    bylawNote:
      "City of Calgary parks bylaws restrict takeoff and landing in parks such as Nose Hill, so flights stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Once the NAV CANADA authorisation is in hand, scheduling turns on weather alone; Hidden Valley, Beddington Heights or Hamptons pair naturally.",
    faqs: [
      {
        question: "What certification does a drone flight in MacEwan Glen require?",
        answer:
          "MacEwan Glen sits about 10 km from Calgary International, inside the CYYC Class C control zone as charted. That means an Advanced RPAS certificate and a NAV CANADA authorisation, obtained before the shoot is confirmed. Fly4MEdia holds the certification and confirms the exact site against NAV Drone before every flight.",
      },
      {
        question: "Can you photograph a home that backs onto Nose Hill Park?",
        answer:
          "Yes. Takeoff and landing stage from the client's property, since City of Calgary bylaws restrict launching in parks, and the flight frames the house against the open grassland from the community side. The park boundary is exactly what an aerial frame proves and a street photograph cannot.",
      },
      {
        question: "How quickly can a MacEwan Glen shoot happen?",
        answer:
          "The community is about 20 minutes from the Calgary base, so scheduling turns on the authorisation and the weather rather than the drive. Once the NAV CANADA filing is in hand, a clear morning is enough — and Hidden Valley or Beddington Heights can share the same mobilisation.",
      },
    ],
    primaryKeywords: [
      "drone photography MacEwan Glen",
      "aerial photography MacEwan Glen",
      "MacEwan Glen drone services",
    ],
    nearestLocations: ["hidden-valley", "beddington-heights", "hamptons"],
  },

  // ──────────────────────────────────────────────────────────── MONTGOMERY ──
  {
    slug: "montgomery",
    name: "Montgomery",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.073, lng: -114.142 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Montgomery is about 11 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Montgomery's grid is caught in a river bend — 45 Street NW and 46 Street NW run downhill until they meet the Bow River Pathway and simply stop.",
      consequence:
        "A property one block from the water photographs, at street level, like a property nowhere near it. The pathway, the river and the approach along Bow Trail NW all vanish below the rooflines.",
      resolution:
        "An approach run flown along the pathway line places the address inside the river bend — water, pathway and property in one continuous move, delivered in the standard turnaround.",
    },
    landmarks: [
      "45 Street NW",
      "46 Street NW",
      "Bow Trail NW",
      "Bow River Pathway",
      "Montgomery Community Centre",
    ],
    terrain:
      "Valley-floor land inside a bend of the Bow — flat along the water, with a short climb rising away from the river on the community's inland side.",
    windNote:
      "Chinook belt, with valley funnelling on top: west wind follows the Bow corridor through Montgomery, and speeds along the river can run above the citywide forecast.",
    seasonalWindow:
      "Cottonwood colour along the Bow River Pathway peaks in a short autumn window, and the river stays open through winter, giving cold-season frames their contrast. Summer evenings put low light straight down the numbered streets.",
    bylawNote:
      "Launching and landing in City of Calgary parks and on the pathway system is restricted by bylaw, so launches stage from private property with permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base — among the shortest mobilisations on the roster. Bowness, Point McKay and Parkdale sit along the same river corridor for paired bookings.",
    faqs: [
      {
        question: "How does Calgary International's control zone affect Montgomery?",
        answer:
          "Montgomery sits about 11 km from Calgary International, near the charted boundary of the CYYC control zone, and which side of the line a given address falls on decides the paperwork. Fly4MEdia plans every Montgomery flight as controlled airspace until NAV Drone shows otherwise, filing any required authorisation before the date is booked.",
      },
      {
        question: "Can aerial photos show how close a Montgomery property is to the river?",
        answer:
          "That is the community's best frame. Montgomery's numbered streets run down to the Bow River Pathway and stop, so a modest altitude puts the water, the pathway and the property in one image. From the kerb the river is invisible; from above it is the whole story.",
      },
    ],
    primaryKeywords: [
      "drone photography Montgomery",
      "aerial photography Montgomery",
      "Montgomery drone services",
    ],
    nearestLocations: ["bowness", "point-mckay", "parkdale"],
  },

  // ──────────────────────────────────────────────────────── MOUNT PLEASANT ──
  {
    slug: "mount-pleasant",
    name: "Mount Pleasant",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.078, lng: -114.088 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "Mount Pleasant sits about 7 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Foothills Medical Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "In early autumn, Winston Golf Club stays green for weeks after the canopy across Mount Pleasant has turned — from the air, the irrigated fairways and the old street trees run on different clocks.",
      consequence:
        "Shot in the wrong month, a listing near Confederation Park looks like any inner-city street. The park adjacency and the canopy that sell the community simply do not read outside their window.",
      resolution:
        "Shoots are timed to the colour window where the listing allows, with a golden-hour hold that sets the property against the park and the fairways — delivered in the standard turnaround.",
    },
    landmarks: [
      "Confederation Park",
      "Winston Golf Club",
      "SAIT Polytechnic",
      "Mount Pleasant Pool",
    ],
    terrain:
      "An inner-city plateau north of the downtown, gently sloping streets broken by the treed band of Confederation Park and the fairways of Winston Golf Club.",
    windNote:
      "Chinook belt. A winter chinook can move surface wind from calm to gusting inside an hour, and the plateau's open blocks offer little shelter when it does.",
    seasonalWindow:
      "Early autumn is the signature window, when the turning canopy plays against green fairways. In winter, Confederation Park and the golf course hold clean snow while the grid goes grey, and low sun rakes long shadows across both.",
    bylawNote:
      "City parks bylaws restrict drone takeoff and landing in Calgary's municipal parks, Confederation Park included; launches are staged from private property with consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Short enough that the schedule is built around light and authorisation rather than driving; Rosemont, Winston Heights and Capitol Hill adjoin.",
    faqs: [
      {
        question: "How close is Mount Pleasant to the airport, and does it matter for drones?",
        answer:
          "About 7 km, and it shapes every flight plan. Mount Pleasant sits inside the CYYC Class C control zone, so work here needs an Advanced RPAS certificate and a NAV CANADA authorisation, filed before the date is confirmed. Flight plans also keep clear of the Foothills Medical Centre heliport about 3 km away.",
      },
      {
        question: "What time of year photographs Mount Pleasant best?",
        answer:
          "Early autumn, when the mature canopy turns and Winston Golf Club's irrigated fairways stay green against it. The contrast lasts a few weeks and shows only from the air. Confederation Park anchors the frame in any season, but the colour window is when the community looks most like itself.",
      },
    ],
    primaryKeywords: [
      "drone photography Mount Pleasant",
      "aerial photography Mount Pleasant",
      "Mount Pleasant drone services",
    ],
    nearestLocations: ["rosemont", "winston-heights-mountview", "capitol-hill"],
  },

  // ──────────────────────────────────────────────────────────── NOLAN HILL ──
  {
    slug: "nolan-hill",
    name: "Nolan Hill",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1797, lng: -114.1908 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "At about 14 km from Calgary International, Nolan Hill runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Nolan Hill keeps its light late: the community stands on high, open ground along Shaganappi Trail NW with a clear western horizon, so low sun rakes across its rooflines after lower communities have gone to shadow.",
      consequence:
        "At midday the repeated rooflines of a new community merge into one texture, and a listing photographed then loses the streetscape depth that is the point of an aerial frame.",
      resolution:
        "Bookings target the last hour, when a golden-hour hold puts raking light across the rooflines and pulls the streets apart — delivered in the standard turnaround.",
    },
    landmarks: [
      "Nolan Hill Boulevard NW",
      "Shaganappi Trail NW",
      "Sage Hill Crossing",
      "Beacon Hill Centre",
    ],
    terrain:
      "High rolling ground at Calgary's northwest corner; streets follow the roll of former ranchland, with open country beginning just past Stoney Trail.",
    windNote:
      "Chinook belt on high, exposed ground. The west wind meets Nolan Hill before most of the city, and the gust spread up here decides flyability more often than the average speed does.",
    seasonalWindow:
      "The community's window is the day's last hour, in every season — low western light separates rooflines that merge at noon. Winter offers it earliest; June stretches it latest.",
    bylawNote:
      "City of Calgary bylaws restrict takeoff and landing in municipal parks and green spaces, so every launch is planned from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the base. Evening bookings suit the community's light, and Sage Hill, Evanston, Kincora or Sherwood can share the same session.",
    faqs: [
      {
        question: "Is authorisation always required for Nolan Hill drone flights?",
        answer:
          "Not always, but it is planned for. At about 14 km from Calgary International, Nolan Hill sits near the charted boundary of the CYYC control zone, and the address decides which side of the line applies. Fly4MEdia assumes controlled airspace until NAV Drone shows otherwise and files before booking.",
      },
      {
        question: "Why are Nolan Hill shoots booked late in the day?",
        answer:
          "Because the light holds here after it has left lower ground. The community stands on high, open terrain with a clear western horizon, so low evening sun rakes across the rooflines and separates streets that read as one mass at noon. The last hour is the working hour.",
      },
      {
        question: "Can Nolan Hill and Sage Hill be covered in one session?",
        answer:
          "Yes. Sage Hill, Evanston, Kincora and Sherwood border the same corridor, and Nolan Hill is about 25 minutes from the Calgary base. Two or three properties across these communities fit inside a single evening light window, which spreads one mobilisation across all of the bookings.",
      },
    ],
    primaryKeywords: [
      "drone photography Nolan Hill",
      "aerial photography Nolan Hill",
      "Nolan Hill drone services",
    ],
    nearestLocations: ["sage-hill", "evanston", "kincora", "sherwood"],
  },

  // ───────────────────────────────────────────────────── NORTHWEST CALGARY ──
  {
    slug: "nw-calgary",
    name: "Northwest Calgary",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1167, lng: -114.15 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Northwest Calgary lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 5 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Northwest Calgary crosses an invisible boundary: communities near Nose Hill Park sit well inside the Calgary International control zone, while the newest streets past Stoney Trail NW run out toward its charted edge.",
      consequence:
        "A client with addresses across the quadrant can collect contradictory answers — one operator quotes a listing on Tuscany Boulevard NW and declines one near Nose Hill Park, because the paperwork genuinely differs between them.",
      resolution:
        "Every address is checked against NAV Drone individually and any authorisation is filed as part of booking, so one process covers the whole quadrant — and the work is delivered in the standard turnaround.",
    },
    landmarks: [
      "Crowfoot Crossing",
      "Beacon Hill Centre",
      "Sage Hill Crossing",
      "Nose Hill Park",
      "Stoney Trail NW",
    ],
    terrain:
      "A rising quadrant — established communities on rolling ground around Nose Hill Park, stepping up to newer high ground along Stoney Trail NW, with open country beyond the ring road.",
    windNote:
      "Chinook belt across the whole quadrant. The higher northwest communities take west wind first and hardest; conditions can differ noticeably between the rim and the lower established streets.",
    seasonalWindow:
      "The quadrant's elevation spread staggers its seasons: snow lingers on the high northwest streets after the established communities are bare, and green-up climbs the same gradient in reverse each spring.",
    bylawNote:
      "Across the quadrant, City of Calgary parks bylaws restrict launching and landing on park and pathway land; private property with the owner's permission is the standard launch site.",
    subjectMix: [
      "luxury-residential",
      "new-build-subdivision",
      "commercial-retail",
      "construction-progress",
    ],
    mobilisationNote:
      "About 20 minutes to the heart of the quadrant. A single session can cover multiple communities from Crowfoot Crossing to Sage Hill Crossing when authorisations are arranged together.",
    faqs: [
      {
        question: "Are drone rules the same across Northwest Calgary?",
        answer:
          "No, and that is the quadrant's defining feature. Communities near Nose Hill Park sit about 10 km from Calgary International, inside the Class C control zone, while newer streets past Stoney Trail NW run toward its charted edge. Every address is checked against NAV Drone, and authorisation is filed wherever required.",
      },
      {
        question: "Can one booking cover several Northwest Calgary properties?",
        answer:
          "Yes, and it is the efficient way to work the quadrant. The base is about 20 minutes away, and a single session can run from Crowfoot Crossing to Sage Hill Crossing inside one light window. Authorisations are filed per site, so the paperwork is arranged once, ahead of the day.",
      },
      {
        question: "What kinds of subjects does aerial work cover in this quadrant?",
        answer:
          "The mix runs from established homes around Nose Hill Park to new construction past Stoney Trail NW, with retail centres like Beacon Hill Centre in between. The same flight discipline covers listings, build-progress series and commercial frames — what changes is the shot list, not the process.",
      },
    ],
    primaryKeywords: [
      "drone photography Northwest Calgary",
      "aerial photography Northwest Calgary",
      "Northwest Calgary drone services",
    ],
    nearestLocations: ["arbour-lake", "tuscany", "royal-oak", "rockland-park"],
  },

  // ──────────────────────────────────────────────────────── PANORAMA HILLS ──
  {
    slug: "panorama-hills",
    name: "Panorama Hills",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.158, lng: -114.135 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Panorama Hills falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The name is literal — Panorama Hills stands on some of the highest ground on Calgary's northern rim, and the same elevation that gives Panamount Boulevard NW its sightlines gives the west wind a clean run at it.",
      consequence:
        "A shoot booked off the citywide forecast can arrive to conditions the rim will not permit — the flight window here is set by gusts on the high ground, not by the number on the weather app.",
      resolution:
        "Bookings are made against the gust spread rather than the average, with a fallback window held, so a windy first date moves the flight without moving the deadline — the work still lands in the standard turnaround.",
    },
    landmarks: [
      "Panamount Boulevard NW",
      "Vivo Recreation Centre",
      "Panorama Hills Park",
      "Country Hills Golf Club",
    ],
    terrain:
      "Elevated rolling ground on the city's northern rim; streets follow the contours, and the land falls toward the green corridor of Country Hills Golf Club.",
    windNote:
      "Chinook belt on exposed elevation. West wind arrives unbroken, and gusts on the rim can exceed the citywide forecast — the reason wind, not paperwork, moves most dates here.",
    seasonalWindow:
      "High ground takes winter early and keeps it late; north-facing slopes hold snow after the rest has gone. Post-chinook days bring the clear, far-carrying air the elevation was named for.",
    bylawNote:
      "Municipal bylaws restrict drone takeoff and landing in City of Calgary parks, so launches are arranged from private property with the client's permission.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Wind, not distance, is the scheduling variable; Country Hills, Harvest Hills and Coventry Hills sit close enough to pair.",
    faqs: [
      {
        question: "Do Panorama Hills drone flights need airport approval?",
        answer:
          "Yes. The community sits about 9 km from Calgary International, inside the Class C control zone as charted, so flights operate under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed. The exact site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Does wind cancel many shoots in Panorama Hills?",
        answer:
          "It moves more dates here than paperwork does. The community stands on exposed high ground in the chinook belt, and gusts on the rim can run past what the citywide forecast suggests. Bookings hold a fallback window, so a windy first date shifts the flight without shifting the deadline.",
      },
    ],
    primaryKeywords: [
      "drone photography Panorama Hills",
      "aerial photography Panorama Hills",
      "Panorama Hills drone services",
    ],
    nearestLocations: ["country-hills", "harvest-hills", "coventry-hills"],
  },

  // ────────────────────────────────────────────────────────────── PARKDALE ──
  {
    slug: "parkdale",
    name: "Parkdale",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.062, lng: -114.12 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "About 10 km from Calgary International, Parkdale falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 1 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Bow runs ice-free past Parkdale through most of the winter, and from the air the open water reads as a dark ribbon against the snow on the flats along Parkdale Boulevard NW.",
      consequence:
        "Sellers assume the cold months are dead time and wait for spring — shooting the community when it looks like everywhere else and listing into the crowded season.",
      resolution:
        "A winter golden-hour hold along the river line uses the open water as the frame's anchor, and the shoot is scheduled for snow and light rather than the calendar — delivered in the standard turnaround.",
    },
    landmarks: [
      "Parkdale Boulevard NW",
      "Bow River Pathway",
      "Parkdale Community Centre",
      "Foothills Medical Centre",
    ],
    terrain:
      "Flat river-bench land between the Bow and the rising ground behind it — some of the most level shooting terrain in the inner city.",
    windNote:
      "Chinook belt, with the Bow corridor adding funnelling: west wind follows the river line past Parkdale, and the flats can gust while sheltered streets nearby stay workable.",
    seasonalWindow:
      "Winter is underrated here — open water against fresh snow is the community's most distinctive frame. Autumn brings a short cottonwood window along the pathway, and summer evenings light the flats end to end.",
    bylawNote:
      "City of Calgary bylaws restrict launching and landing in parks and along the pathway system, so flights over the river corridor stage from private property with consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base along the river. St. Andrew's Heights, Point McKay and Banff Trail adjoin, so multi-property sessions are simple to arrange.",
    faqs: [
      {
        question: "What does the Foothills heliport mean for Parkdale drone work?",
        answer:
          "It is part of every flight plan. Parkdale sits about 10 km from Calgary International, inside the CYYC Class C control zone, so an Advanced RPAS certificate and a NAV CANADA authorisation come first. The Foothills Medical Centre heliport operates about 1 km away, and routes are planned clear of its approaches.",
      },
      {
        question: "Is winter a poor season for aerial photography in Parkdale?",
        answer:
          "Usually the opposite. The Bow stays open through most of the winter, and dark water against snow-covered flats is the most distinctive frame this community offers. Flat grey days are avoided; a clear, cold morning with fresh snow along the Bow River Pathway is worth waiting for.",
      },
    ],
    primaryKeywords: [
      "drone photography Parkdale",
      "aerial photography Parkdale",
      "Parkdale drone services",
    ],
    nearestLocations: ["st-andrews-heights", "point-mckay", "banff-trail"],
  },

  // ─────────────────────────────────────────────────────────── POINT MCKAY ──
  {
    slug: "point-mckay",
    name: "Point McKay",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.068, lng: -114.131 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Point McKay sits about 10 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Foothills Medical Centre heliport operates about 0 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Point McKay lives in the wind's corridor: the Bow valley narrows past Edworthy Park, and west wind that barely registers on higher ground runs the river line along Point McKay Crescent NW.",
      consequence:
        "A flight planned off the citywide forecast can meet a corridor gusting past its limits at the waterline, and the riverside frames that justify the booking are the first thing cut.",
      resolution:
        "Sessions are set for the corridor's calm hours — usually early — with the valley's own wind checked against limits before launch, and the full riverside shot list delivered in the standard turnaround.",
    },
    landmarks: [
      "Point McKay Crescent NW",
      "Bow River Pathway",
      "Edworthy Park",
      "Foothills Medical Centre",
    ],
    terrain:
      "A narrow band of riverside land pinched between the Bow and rising ground, with the slopes of Edworthy Park directly across the water.",
    windNote:
      "Chinook belt with pronounced valley funnelling. The narrowing Bow corridor accelerates west wind past Point McKay, so conditions at the waterline are read separately from the citywide picture.",
    seasonalWindow:
      "The corridor photographs best in the calm of early morning, in any season. Autumn colours the pathway's cottonwoods; winter keeps the Bow open and dark against snow; the slopes across the river hold white long after the flats clear.",
    bylawNote:
      "Takeoff and landing in City of Calgary parks and on pathways is restricted by bylaw; launches are staged from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Early slots suit the corridor's calm air, and Parkdale, Montgomery or St. Andrew's Heights can share the same morning.",
    faqs: [
      {
        question: "Can you fly a drone next to the Foothills Medical Centre heliport?",
        answer:
          "Yes, under discipline. Point McKay sits inside the CYYC Class C control zone about 10 km from Calgary International, so flights need an Advanced RPAS certificate and a NAV CANADA authorisation. The Foothills Medical Centre heliport is immediately beside the community, and every route is planned to keep clear of its approaches.",
      },
      {
        question: "When is the river corridor at Point McKay calm enough to fly?",
        answer:
          "Most reliably early in the day. The Bow valley narrows past Edworthy Park and funnels west wind through the corridor, so afternoons can gust at the waterline while higher ground sits calm. Bookings are planned against the valley's own conditions rather than the citywide forecast, with a fallback window held.",
      },
      {
        question: "What does aerial photography add for a riverside property here?",
        answer:
          "Position. Point McKay is a narrow band between the Bow and the rising ground behind it, and an elevated frame shows the water, the Bow River Pathway and the slopes of Edworthy Park in one composition. No street-level photograph can place a property inside that geography.",
      },
    ],
    primaryKeywords: [
      "drone photography Point McKay",
      "aerial photography Point McKay",
      "Point McKay drone services",
    ],
    nearestLocations: ["parkdale", "montgomery", "st-andrews-heights"],
  },

  // ──────────────────────────────────────────────────────────── RANCHLANDS ──
  {
    slug: "ranchlands",
    name: "Ranchlands",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1183, lng: -114.1636 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Ranchlands is about 10 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Ranchlands was planned before Calgary started building its suburbs around water, and from the air the difference is plain — no pond or creek anchors the frame, just rolling streets running toward the grassland of Nose Hill Park.",
      consequence:
        "An operator who leans on the default suburban formula — pond reflection at golden hour — arrives here with a shot list the geography cannot honour.",
      resolution:
        "Composition is built from what the community actually offers: a boundary-line orbit that sets the rolling streets against Nose Hill Park's grassland, flown in low light and delivered in the standard turnaround.",
    },
    landmarks: [
      "Ranchlands Boulevard NW",
      "Ranchlands Community Centre",
      "Ranchlands Off-Leash Park",
      "Nose Hill Park",
    ],
    terrain:
      "Rolling former ranchland west of Nose Hill Park; streets follow the contour lines, and green corridors rather than water thread the community together.",
    windNote:
      "Chinook belt on open upland. There is no named valley or ridge to bend the flow here — wind arrives much as forecast, which at least makes the go or no-go call a clean one.",
    seasonalWindow:
      "Green-up is brief on this dry upland — parks and lawns hold colour into summer while the grassland beyond turns gold early. Fresh snow simplifies the frame and makes the contour roll of the streets legible.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone launches in municipal parks, the off-leash grounds included, so takeoff is planned from private property with consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the base. A straightforward same-day candidate, with Edgemont, Citadel and Hamptons nearby for paired bookings.",
    faqs: [
      {
        question: "What airspace applies to drone flights in Ranchlands?",
        answer:
          "Ranchlands lies about 10 km from Calgary International, near the charted boundary of the CYYC control zone — close enough that the specific address decides whether authorisation applies. Fly4MEdia plans the flight as controlled airspace until NAV Drone shows otherwise for the site, and files any required authorisation before the date is booked.",
      },
      {
        question: "Ranchlands has no lake — what anchors an aerial photo here?",
        answer:
          "The green structure does. With no open water in the frame, composition leans on the community's park spine — Ranchlands Off-Leash Park and the community centre grounds — and on Nose Hill Park's grassland as a backdrop. Those anchors photograph best in low light, when shadow gives the rolling ground its shape.",
      },
    ],
    primaryKeywords: [
      "drone photography Ranchlands",
      "aerial photography Ranchlands",
      "Ranchlands drone services",
    ],
    nearestLocations: ["edgemont", "citadel", "hamptons"],
  },

  // ───────────────────────────────────────────────────────── ROCKLAND PARK ──
  {
    slug: "rockland-park",
    name: "Rockland Park",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1606, lng: -114.2222 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Mixed — CYBW control zone boundary",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "About 5 km from Bearspaw and in the same airspace corridor, Rockland Park is planned as controlled airspace. Operations run under an Advanced RPAS certificate, and a NAV CANADA RPAS authorisation is obtained wherever the specific site calls for one. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Rockland Park is a community with an edge: its streets run level along Rockland Park Drive NW until the land stops at the Bow River bluff and the Bow valley opens below the rim.",
      consequence:
        "From the street, a home near the rim looks like any other new build — the drop, and the valley view that priced the lot, sit hidden behind the last row of rooftops.",
      resolution:
        "A rising reveal flown off the bluff line lifts from streetscape to open valley in one move and shows what the address actually owns, delivered in the standard turnaround.",
    },
    landmarks: [
      "Rockland Park Drive NW",
      "Bow River bluff",
      "Stoney Trail",
      "Crowfoot Crossing",
    ],
    terrain:
      "A new-build plateau beside Stoney Trail — level ground that ends abruptly at the Bow River bluff, with the valley falling away below the community's rim.",
    windNote:
      "Chinook belt at a valley rim. West wind climbs out of the Bow valley and crests the bluff, so the community's western edge can be sharply gustier than streets a few blocks in.",
    seasonalWindow:
      "The rim catches the day's last light over the valley, which is when the bluff line photographs best. In a community still building out, the working season for progress series runs from spring thaw to freeze-up.",
    bylawNote:
      "City of Calgary bylaws restrict takeoff and landing on municipal park land, so launches stage from private property — in a building community, usually the client's own site — with consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "luxury-residential"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Tuscany, Royal Oak and Crestmont adjoin, and Cochrane is a short run further west — pairing communities shares the mobilisation.",
    faqs: [
      {
        question: "Which airport governs the airspace above Rockland Park?",
        answer:
          "Springbank, not Calgary International. Rockland Park sits about 12 km from Springbank Airport in a corridor planned as controlled airspace, so flights run under an Advanced RPAS certificate, with NAV CANADA authorisation obtained wherever the specific site requires it. Every site and altitude is confirmed against NAV Drone before flight.",
      },
      {
        question: "Can aerial photos prove a Rockland Park view before a buyer visits?",
        answer:
          "Yes — that is the community's core assignment. The streets run level until the land stops at the Bow River bluff, so the drop and the valley beyond hide behind the last row of rooftops. A rising reveal from street height to the bluff line shows exactly what the address owns.",
      },
      {
        question: "Can a Rockland Park booking include Tuscany or Cochrane?",
        answer:
          "Comfortably. Rockland Park is about 25 minutes from the Calgary base, with Tuscany, Royal Oak and Crestmont beside it and Cochrane a short run further west. Pairing two communities in one session shares the mobilisation, which is the practical way to book smaller shoots on this edge of the city.",
      },
    ],
    primaryKeywords: [
      "drone photography Rockland Park",
      "aerial photography Rockland Park",
      "Rockland Park drone services",
    ],
    nearestLocations: ["crestmont", "royal-oak", "tuscany", "cochrane"],
  },
// ───────────────────────────────────────────────────────── ROCKY RIDGE ──
  {
    slug: "rocky-ridge",
    name: "Rocky Ridge",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1394, lng: -114.2347 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYBW control zone boundary",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Rocky Ridge sits about 11 km from Springbank Airport, near the charted boundary of the CYBW control zone. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Twelve Mile Coulee holds the one open water line in Rocky Ridge's frame — a creek thread along the ravine bottom that runs dark against pale grass and marks the community's edge more clearly than any road does.",
      consequence:
        "Ground photography cannot use it. From the street the coulee is a gap between fences; the water, the ravine and the edge-of-city position that buyers pay for here simply never appear.",
      resolution:
        "A boundary-line orbit flown from a private launch puts the ravine, the rooflines and the foothill horizon into one frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Twelve Mile Coulee",
      "YMCA Rocky Ridge",
      "Rocky Ridge Road NW",
      "Crowfoot Crossing",
      "Royal Oak Centre",
    ],
    terrain:
      "A high bench on the city's north-west shoulder: level streets on top, ground breaking away into the ravine system of Twelve Mile Coulee, and long sightlines west toward the foothills.",
    windNote:
      "Chinook belt, and exposed for it. The community's high western edge takes the west wind before most of the city, so the gust spread gets checked against the forecast average before a flight is confirmed.",
    seasonalWindow:
      "Evening light is the asset — the high ground faces the foothill horizon, so golden hour runs long and clean. Snow lingers on the shaded ravine slopes after the streets above are bare.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Comfortably a same-day booking, and easily paired with Royal Oak, Tuscany or Arbour Lake in a single mobilisation.",
    faqs: [
      {
        question: "Which airport controls the airspace over Rocky Ridge?",
        answer:
          "Springbank, not Calgary International. Rocky Ridge sits about 11 km from Springbank Airport, near the charted edge of the CYBW control zone. Flights are planned as controlled airspace until NAV Drone shows otherwise for the specific site, and any required authorisation is filed before the date is booked.",
      },
      {
        question: "Can aerial photos of a Rocky Ridge home include Twelve Mile Coulee?",
        answer:
          "Usually, yes. The bylaw restriction covers taking off and landing in municipal parks, not photographing them from authorised airspace. Launching from the client's property, an orbit can carry the ravine, the rooflines and the foothill horizon in one frame without touching park ground.",
      },
    ],
    primaryKeywords: [
      "drone photography Rocky Ridge",
      "aerial photography Rocky Ridge",
      "Rocky Ridge drone services",
    ],
    nearestLocations: ["royal-oak", "tuscany", "arbour-lake", "rockland-park"],
  },

  // ──────────────────────────────────────────────────────────── ROSEMONT ──
  {
    slug: "rosemont",
    name: "Rosemont",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.085, lng: -114.094 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 7 km from Calgary International, Rosemont falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 4 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Rosemont still runs on the original survey grid — 4 Street NW and 28 Avenue NW cross at true right angles — and from altitude that grid stops dead where Confederation Park cuts a green diagonal across it.",
      consequence:
        "That is exactly the context an infill listing needs and a ground camera cannot get: which corner of the grid the house holds, how close the park's edge really is, and what the lot actually backs onto.",
      resolution:
        "A rising reveal from roof height places the property on its grid with the park in frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "Confederation Park",
      "SAIT Polytechnic",
      "Winston Golf Club",
      "4 Street NW",
      "Regal Crescent NW",
    ],
    terrain:
      "An inner-city plateau north of downtown: a level post-war grid under a mature tree canopy, with the coulee that Confederation Park occupies providing the only real fold in the ground.",
    windNote:
      "Chinook belt like the rest of the city. The plateau has no particular exposure, and the mature canopy takes some edge off surface gusts without changing conditions at flying height.",
    seasonalWindow:
      "The canopy sets the calendar. Leaf-on from late spring hides lot lines and softens the grid; leaf-off makes the street pattern legible again, and winter's low sun rakes it cleanly.",
    bylawNote:
      "Calgary's parks bylaws do not allow drone launches or landings on municipal park land — Confederation Park included — so takeoff is staged from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — close enough for short-notice work, and readily paired with Mount Pleasant, Winston Heights or Cambrian Heights in one visit.",
    faqs: [
      {
        question: "Is drone photography allowed in Rosemont given how close the airport is?",
        answer:
          "Yes, with paperwork. Rosemont is about 7 km from Calgary International, inside the CYYC Class C control zone. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, and the site and altitude are checked against NAV Drone first.",
      },
      {
        question: "Does the Foothills Medical Centre heliport affect shoots in Rosemont?",
        answer:
          "It shapes the flight plan rather than blocking it. The heliport operates about 4 km away, and Rosemont flight plans keep clear of its approaches. That is handled in planning; on the day it simply means the route around your property is fixed before launch.",
      },
      {
        question: "What does an aerial add for a Rosemont infill listing?",
        answer:
          "Context the street cannot show: the lot's position on the grid, the real distance to Confederation Park and Winston Golf Club, and how the mature canopy frames the block. On a plateau of similar rooflines, that placement is often the story the listing needs.",
      },
    ],
    primaryKeywords: [
      "drone photography Rosemont",
      "aerial photography Rosemont",
      "Rosemont drone services",
    ],
    nearestLocations: ["mount-pleasant", "winston-heights-mountview", "cambrian-heights"],
  },

  // ─────────────────────────────────────────────────────────── ROYAL OAK ──
  {
    slug: "royal-oak",
    name: "Royal Oak",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.15, lng: -114.2333 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Mixed — CYBW control zone boundary",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Royal Oak shares its airspace corridor with Bearspaw, about 3 km away, and flights through it are planned as controlled airspace. An Advanced RPAS certificate covers the work, with NAV CANADA RPAS authorisation obtained wherever the specific site requires it. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From altitude Royal Oak reads as a set of nested loops — Royal Oak Drive NW rings the community and feeds every court inward — with the retail planes of Beacon Hill Centre and Royal Oak Centre pinned at its outer edges.",
      consequence:
        "On the ground every curve looks the same, which is the standing problem with loop-and-court suburbs: nothing in a street-level frame says where in the community the property actually sits.",
      resolution:
        "A rising reveal that lifts from the front elevation to loop height fixes the address inside its ring and shows the run to the shops, delivered in the standard turnaround.",
    },
    landmarks: [
      "Royal Oak Drive NW",
      "Beacon Hill Centre",
      "Royal Oak Centre",
      "Royal Oak Park",
      "Royal Birch Boulevard NW",
    ],
    terrain:
      "Rolling upland at the city's north-west edge — gentle rises, stormwater ponds and broad loop streets rather than sharp relief, with the foothills holding the western horizon.",
    windNote:
      "Squarely in the chinook belt. Open upland with little to break a west wind, so winter shoots are planned around gust spread rather than the daily average.",
    seasonalWindow:
      "Open rooflines and pale streets read best under low sun — early morning or the last two hours of light. Nothing stands between the upland and the western horizon, so the golden hour runs its full length.",
    bylawNote:
      "Drone takeoff and landing inside City of Calgary parks — Royal Oak Park among them — is restricted by bylaw, so flights launch from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. A comfortable same-day booking, and one mobilisation can cover Royal Oak together with Arbour Lake, Tuscany or Rocky Ridge.",
    faqs: [
      {
        question: "Does flying a drone in Royal Oak need NAV CANADA approval?",
        answer:
          "It depends on the exact address. Royal Oak sits in the corridor near the Springbank control zone boundary, so flights are planned as controlled airspace under an Advanced RPAS certificate, with authorisation obtained wherever the site requires one. NAV Drone settles the question for the specific property before every flight.",
      },
      {
        question: "Why do aerial photos matter for a loop-street community like Royal Oak?",
        answer:
          "Because street-level frames all look alike there. From above, the rings of Royal Oak Drive NW give a property a legible position — which loop, which court, how far to Royal Oak Centre or Beacon Hill Centre. That orientation is the first thing a buyer from outside the quadrant wants.",
      },
    ],
    primaryKeywords: [
      "drone photography Royal Oak",
      "aerial photography Royal Oak",
      "Royal Oak drone services",
    ],
    nearestLocations: ["arbour-lake", "tuscany", "rocky-ridge", "rockland-park"],
  },

  // ─────────────────────────────────────────────────────────── SAGE HILL ──
  {
    slug: "sage-hill",
    name: "Sage Hill",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1664, lng: -114.1631 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "Sage Hill sits about 12 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Sage Hill folds its streets around standing water — the wetland ponds in the ravine below Sage Hill Drive NW hold through most of the year, draining toward the valley Symons Valley Ranch takes its name from.",
      consequence:
        "The ponds are the community's best feature and its hardest one to photograph: from the sidewalk they sit below grade and out of sight, so listings read as ordinary new-build streetscape.",
      resolution:
        "A golden-hour hold over the client's property with the wetland in the middle distance puts the water to work, delivered in the standard turnaround.",
    },
    landmarks: [
      "Sage Hill Crossing",
      "Symons Valley Ranch",
      "Sage Hill Drive NW",
      "Sage Valley Boulevard NW",
      "Beacon Hill Centre",
    ],
    terrain:
      "New-suburb hills folded around a broad ravine: streets ride the high ground off Sage Valley Boulevard NW while the low ground stays wet, green and unbuilt.",
    windNote:
      "Chinook belt. The ravine can channel a west wind along its axis, so conditions over the wetland are read separately from the high ground before any low pass over the ponds.",
    seasonalWindow:
      "Ice-off in spring turns the ponds from white patches to mirrors, and they hold reflections through summer evenings. Autumn browns the ravine grass first while the streets above stay tidy.",
    bylawNote:
      "City of Calgary bylaws keep drone takeoff and landing out of municipal parks and environmental reserve, so launches are staged from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — a routine same-day booking, and naturally paired with Nolan Hill, Kincora or Evanston in a single trip north.",
    faqs: [
      {
        question: "Is Sage Hill inside Calgary International's controlled airspace?",
        answer:
          "It sits right at the question's edge. Sage Hill is about 12 km from Calgary International, near the charted CYYC boundary, and which side of the line an address falls on decides the paperwork. Flights are planned as controlled until NAV Drone shows otherwise, with authorisation filed before booking.",
      },
      {
        question: "Can the Sage Hill wetlands appear in a listing's aerial photos?",
        answer:
          "Yes. Takeoff and landing stay on private property, but photographing the ravine from authorised airspace is routine. Most Sage Hill briefs place the ponds in the middle distance behind the home — the frame that separates this community from every other new-build suburb nearby.",
      },
    ],
    primaryKeywords: [
      "drone photography Sage Hill",
      "aerial photography Sage Hill",
      "Sage Hill drone services",
    ],
    nearestLocations: ["nolan-hill", "evanston", "kincora", "nw-calgary"],
  },

  // ──────────────────────────────────────────────────────── SCENIC ACRES ──
  {
    slug: "scenic-acres",
    name: "Scenic Acres",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1245, lng: -114.21 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 14,
      authorizationRequired: true,
      authorizationNote:
        "Scenic Acres is about 14 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The name is a land-title fact: the benches along Scenic Ridge Way NW tip south-west toward the river valley, and from altitude the water below and the mountain horizon behind it enter the frame together.",
      consequence:
        "That double view is what the community trades on, and it is precisely what a ground-level camera loses — from the street there is a fence, a roofline, and no evidence of what the property looks over.",
      resolution:
        "A rising reveal to bench height restores the valley and the horizon in a single move, delivered in the standard turnaround.",
    },
    landmarks: [
      "Scenic Acres Drive NW",
      "Scenic Acres Park",
      "Nose Hill Park",
      "Crowchild Trail access",
      "Scenic Ridge Way NW",
    ],
    terrain:
      "A rim community on the city's north-west bench: level on top, stepping down toward the river valley to the south-west, with Nose Hill Park far enough east to sit on the skyline rather than in it.",
    windNote:
      "Chinook belt, with genuine exposure on the rim. West wind arrives unbroken off the foothills, so the bench edge is flown on the forecast's gust numbers, not its averages.",
    seasonalWindow:
      "An evening community: low western sun rakes across the benches and drops the valley into relief. In winter the south-west aspect clears the streets while the slope below keeps its snow.",
    bylawNote:
      "Municipal parks in Calgary — Scenic Acres Park included — are closed to drone takeoff and landing under the parks bylaws, so launches are made from private property with the client's consent.",
    subjectMix: ["luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Easy same-day work, and one mobilisation covers Scenic Acres alongside Hawkwood, Silver Springs or Edgemont.",
    faqs: [
      {
        question: "Do you need airspace clearance to fly a drone over Scenic Acres?",
        answer:
          "Sometimes — the community sits about 14 km from Calgary International, close to the charted edge of the CYYC control zone, and the exact address decides it. Flights are planned as controlled airspace until NAV Drone shows otherwise, and any authorisation is filed before the shoot date is booked.",
      },
      {
        question: "What makes Scenic Acres worth aerial photography rather than ground shots alone?",
        answer:
          "The view is the value. Scenic Acres properties sell on what they overlook — the river valley and the mountain horizon — and neither appears in a street-level frame. From bench height both return, and the property's position on the rim becomes the photograph's argument.",
      },
    ],
    primaryKeywords: [
      "drone photography Scenic Acres",
      "aerial photography Scenic Acres",
      "Scenic Acres drone services",
    ],
    nearestLocations: ["hawkwood", "silver-springs", "edgemont"],
  },

  // ──────────────────────────────────────────────────────────── SHERWOOD ──
  {
    slug: "sherwood",
    name: "Sherwood",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.158, lng: -114.155 },
    tier: 3,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Sherwood sits about 11 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Sherwood hangs off one spine: Sherwood Drive NW carries every court in the community, and from altitude the pocket reads as sealed — bounded along one edge by the flat big-box roof plane of Beacon Hill Shopping Centre.",
      consequence:
        "A buyer scanning ground photos cannot tell Sherwood's courts apart or see how self-contained the pocket is — the quiet that comes from having no through traffic never makes it into the frame.",
      resolution:
        "A boundary-line orbit at a few hundred feet shows the sealed edges, the single spine and the shopping centre a short run away, delivered in the standard turnaround.",
    },
    landmarks: [
      "Sherwood Drive NW",
      "Beacon Hill Shopping Centre",
      "Sherwood Park",
      "Nose Hill Park",
    ],
    terrain:
      "Gently rolling upland at the city's northern shoulder — modest relief inside the community itself, with the long views belonging to Nose Hill Park rising on the south-east skyline.",
    windNote:
      "Chinook belt. Open upland without meaningful shelter, so winter windows are picked on gust spread; a calm average with a wide spread is still a no-fly.",
    seasonalWindow:
      "Newer rooflines and light streets photograph best under low-angle sun — the first and last hours. On the open upland the golden hour arrives ungated by terrain and runs its full published length.",
    bylawNote:
      "City of Calgary parks bylaws bar drone takeoff and landing in municipal parks, Sherwood Park included, so flights are launched from private property with the client's consent.",
    subjectMix: ["new-build-subdivision", "luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base — same-day territory, and easily combined with Kincora, Symons Valley or Panorama Hills on the same run.",
    faqs: [
      {
        question: "Whose airspace is Sherwood in — does the airport matter this far north-west?",
        answer:
          "It matters. Sherwood is about 11 km from Calgary International, near the charted CYYC control zone boundary, and which side of the line a given address falls on decides the paperwork. Flights are planned as controlled until NAV Drone shows otherwise, and any authorisation is filed before booking.",
      },
      {
        question: "Is Sherwood too small a community to justify aerial photography?",
        answer:
          "The opposite — small, sealed pockets benefit most. One orbit captures the whole community: the single spine of Sherwood Drive NW, the sealed edges, and Beacon Hill Shopping Centre nearby. For a listing, that orientation does work a dozen interior photos cannot.",
      },
    ],
    primaryKeywords: [
      "drone photography Sherwood",
      "aerial photography Sherwood",
      "Sherwood drone services",
    ],
    nearestLocations: ["kincora", "symons-valley", "panorama-hills"],
  },

  // ────────────────────────────────────────────────────── SILVER SPRINGS ──
  {
    slug: "silver-springs",
    name: "Silver Springs",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.114, lng: -114.198 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "At about 13 km from Calgary International, Silver Springs runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Silver Springs faces its evenings: the community sits on a south-facing bench above Bowmont Park, so the day's last light comes up the valley and rakes along the fairways of Silver Springs Golf Course instead of dying flat.",
      consequence:
        "Shot at midday, the same ground turns into a grey grid — the slope, the fairway green and the river below all flatten, and the listing loses the one aspect the community is organised around.",
      resolution:
        "A golden-hour hold timed to that raking light, flown from a private launch above the escarpment, keeps the valley in relief, delivered in the standard turnaround.",
    },
    landmarks: [
      "Silver Springs Golf Course",
      "Bowmont Park",
      "Bow River Pathway",
      "Silvergrove Drive NW",
      "Silver Springs Community Centre",
    ],
    terrain:
      "A south-facing bench above the river: level residential ground on top, then a wooded escarpment dropping through Bowmont Park to the flats and the Bow River Pathway.",
    windNote:
      "Chinook belt, with an escarpment complication: wind coming over the valley edge behaves differently at the rim than on the flat streets above, so the slope is read separately before any low pass.",
    seasonalWindow:
      "The south aspect earns its keep in winter — low sun still reaches the bench when north-facing slopes have gone flat. Green-up along the golf course marks the start of the community's best months.",
    bylawNote:
      "Under Calgary's parks bylaws, municipal parks and pathways — Bowmont Park among them — are not available for drone takeoff or landing; launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 20 minutes from the Calgary base. Comfortable same-day scheduling, and pairs naturally with Scenic Acres, Hawkwood or Valley Ridge in one mobilisation.",
    faqs: [
      {
        question: "Can drones operate over Silver Springs without special permission?",
        answer:
          "Sometimes yes, sometimes no — Silver Springs sits about 13 km from Calgary International, close to the charted CYYC boundary, and the specific address decides which rules apply. Flights are planned as controlled airspace until NAV Drone shows otherwise, with any authorisation filed before the date is booked.",
      },
      {
        question: "When should a Silver Springs property be photographed from the air?",
        answer:
          "Late in the day. The community occupies a south-facing bench above Bowmont Park, and the low evening light raking up the valley is what gives the slope, the fairways and the river flats their depth. Midday sun flattens exactly the features the location sells.",
      },
    ],
    primaryKeywords: [
      "drone photography Silver Springs",
      "aerial photography Silver Springs",
      "Silver Springs drone services",
    ],
    nearestLocations: ["scenic-acres", "hawkwood", "valley-ridge"],
  },

  // ───────────────────────────────────────────────── ST. ANDREWS HEIGHTS ──
  {
    slug: "st-andrews-heights",
    name: "St. Andrews Heights",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.064, lng: -114.137 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "At about 11 km from Calgary International, St. Andrews Heights runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The 'Heights' is measurable from the air: St. Andrews Heights ends in an escarpment above the Bow River Pathway, with a full bench of drop between the back streets and the water below.",
      consequence:
        "Ground photography records a quiet grid and nothing else. The elevation — the reason these blocks carry the value they do — sits entirely below the camera's line of sight.",
      resolution:
        "A rising reveal off the escarpment edge restores the drop, the river and the valley in one continuous move, delivered in the standard turnaround.",
    },
    landmarks: [
      "Foothills Medical Centre",
      "Alberta Children's Hospital",
      "Bow River Pathway",
      "University of Calgary",
      "St. Andrews Place NW",
    ],
    terrain:
      "An escarpment-edge bench: level, tree-lined streets off St. Andrews Place NW that end where the ground breaks away to the Bow River Pathway and the flats along the water.",
    windNote:
      "Chinook belt, sharpened by the valley: west wind funnels along the Bow and climbs the escarpment face, so the edge is flown on its own wind reading, not the citywide forecast.",
    seasonalWindow:
      "Evening light travels the valley and lights the escarpment face late — the community's best window. Spring green-up runs up the slope over a couple of weeks, valley floor first, bench last.",
    bylawNote:
      "Calgary's parks bylaws restrict drone takeoff and landing on municipal park land and pathways, so St. Andrews Heights flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — short-notice friendly, and readily combined with Parkdale, Point McKay or University Heights in a single visit.",
    faqs: [
      {
        question: "Is St. Andrews Heights inside Calgary's controlled airspace?",
        answer:
          "It sits close enough that the answer depends on the address. St. Andrews Heights is about 11 km from Calgary International, near the charted CYYC control zone boundary. Flights are planned as controlled airspace until NAV Drone shows otherwise for the specific site, and any required authorisation is filed before the date is booked.",
      },
      {
        question: "What does elevation add to a St. Andrews Heights listing?",
        answer:
          "Everything the street hides. These blocks sit on a bench above the Bow River Pathway, and the drop to the water is the location's core asset. An aerial restores that relationship — house, escarpment, river — in a single frame no ground camera can compose.",
      },
    ],
    primaryKeywords: [
      "drone photography St. Andrews Heights",
      "aerial photography St. Andrews Heights",
      "St. Andrews Heights drone services",
    ],
    nearestLocations: ["parkdale", "point-mckay", "university-heights"],
  },

  // ─────────────────────────────────────────────────────── SYMONS VALLEY ──
  {
    slug: "symons-valley",
    name: "Symons Valley",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.166, lng: -114.145 },
    tier: 3,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Symons Valley lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The name is literal: Symons Valley Road NW runs the floor of a real valley, Nose Creek Pathway follows the water beside it, and the new streets off Symons Valley Parkway NW step up the shoulders on either side.",
      consequence:
        "For construction and new-community work that grade is the story — how far a phase has climbed the shoulder, what drains where — and it is unreadable from a truck window on the valley floor.",
      resolution:
        "A repeat progress series flown on fixed waypoints records each phase against the valley's grade month over month, delivered in the standard turnaround.",
    },
    landmarks: [
      "Symons Valley Ranch",
      "Nose Creek Pathway",
      "Vivo Recreation Centre",
      "Symons Valley Road NW",
      "144 Avenue NW",
    ],
    terrain:
      "A working valley: creek flats along Nose Creek Pathway, new residential ground stepping up both shoulders, and open country still holding the frame's northern edge.",
    windNote:
      "Chinook belt with valley behaviour: the trench channels and accelerates a west or north-west wind along its axis, so the floor and the shoulders often need two different wind calls.",
    seasonalWindow:
      "Construction season sets the rhythm — bare grading reads best in spring and fall, when low sun models the valley's relief instead of flattening it.",
    bylawNote:
      "City of Calgary bylaws keep drone launches and landings out of municipal parks and off pathways, so operations here stage from private or client-controlled land with consent.",
    subjectMix: ["new-build-subdivision", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base. Routine as a same-day booking, and pairs cleanly with Carrington, Kincora or Sherwood on the same trip.",
    faqs: [
      {
        question: "Does a Symons Valley shoot need airport authorisation first?",
        answer:
          "Yes. Symons Valley lies inside the CYYC control zone, about 10 km from Calgary International, so flights are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. The specific site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "Can you document a construction project in Symons Valley over several months?",
        answer:
          "That is the natural assignment here. Fixed waypoints and matched altitudes make each visit comparable, so a phase's climb up the valley shoulder reads clearly across the series. Authorisation and the NAV Drone check are repeated for every flight, never carried over from the last one.",
      },
    ],
    primaryKeywords: [
      "drone photography Symons Valley",
      "aerial photography Symons Valley",
      "Symons Valley drone services",
    ],
    nearestLocations: ["carrington", "kincora", "sherwood"],
  },

  // ───────────────────────────────────────────────────────── THORNCLIFFE ──
  {
    slug: "thorncliffe",
    name: "Thorncliffe",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.103, lng: -114.07 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 5,
      authorizationRequired: true,
      authorizationNote:
        "About 5 km from Calgary International, Thorncliffe falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Thorncliffe loses the sun before the almanac says it should: Nose Hill Park stands directly west, and on winter evenings the hill's shadow crosses the rooftops along Thornhill Drive NW well ahead of the published sunset.",
      consequence:
        "A golden-hour shoot scheduled off the sunset table arrives to find the light already gone — the frames come back flat, and the reshoot costs a week of calendar.",
      resolution:
        "A golden-hour hold scheduled against the hill rather than the almanac, confirmed on site, catches the last direct light, delivered in the standard turnaround.",
    },
    landmarks: [
      "Nose Hill Park",
      "Thorncliffe Shopping Centre",
      "Deerfoot Trail access",
      "Thornhill Drive NW",
    ],
    terrain:
      "East-sloping ground off the shoulder of Nose Hill Park — a steady, even grade running down toward the Deerfoot corridor rather than any single dramatic drop.",
    windNote:
      "Chinook belt. Nose Hill stands between the community and the west wind, which softens surface gusts on the streets without changing what the airframe meets at height.",
    seasonalWindow:
      "Morning is reliable — the eastern horizon is open and first light reaches the whole slope at once. Evenings shorten in winter as the hill's shadow arrives early; summer's high sun clears it.",
    bylawNote:
      "Drone takeoff and landing in Calgary's municipal parks — Nose Hill Park included — is restricted by the parks bylaws, so launches are staged from private property with the client's consent.",
    subjectMix: ["commercial-retail", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base, so short-notice bookings are practical. Pairs easily with Huntington Hills, Highwood or Beddington Heights in one mobilisation.",
    faqs: [
      {
        question: "Is it legal to fly a drone in Thorncliffe so close to the airport?",
        answer:
          "Yes, under the right certificate. Thorncliffe sits about 5 km from Calgary International, inside the CYYC control zone, so every flight requires an Advanced RPAS certificate and a NAV CANADA authorisation obtained before the shoot is confirmed. The site and altitude are verified against NAV Drone beforehand.",
      },
      {
        question: "Why not book a Thorncliffe shoot for the hour before sunset?",
        answer:
          "Because Nose Hill Park takes the light first. The hill stands immediately west of the community, and in the cold months its shadow reaches the rooftops well before the published sunset. Shoots here are timed against the hill's shadow line, not the almanac.",
      },
    ],
    primaryKeywords: [
      "drone photography Thorncliffe",
      "aerial photography Thorncliffe",
      "Thorncliffe drone services",
    ],
    nearestLocations: ["huntington-hills", "highwood", "beddington-heights"],
  },

  // ───────────────────────────────────────────────────────────── TUSCANY ──
  {
    slug: "tuscany",
    name: "Tuscany",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.1289, lng: -114.2389 },
    tier: 2,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 16,
      authorizationRequired: true,
      authorizationNote:
        "At about 16 km from Calgary International, Tuscany runs close to the charted edge of the CYYC control zone — which side of the line an address falls on decides the paperwork. Planning treats it as controlled airspace until NAV Drone shows otherwise, and any required authorisation is filed before the date is booked. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Tuscany's rooflines stop in a hard line where Twelve Mile Coulee Natural Area begins — a single-era community with an abrupt natural boundary, and from altitude that edge, not the entrance sign, is what locates it.",
      consequence:
        "For a listing near that edge, ground photos flatten the community's best fact: nothing at street level shows that the ravine, not another row of houses, is what the property backs onto.",
      resolution:
        "A boundary-line orbit along the coulee rim frames the home against the natural area and the foothill horizon, delivered in the standard turnaround.",
    },
    landmarks: [
      "Twelve Mile Coulee Natural Area",
      "Tuscany Club",
      "Tuscany LRT",
      "Tuscany Market",
      "Tuscany Boulevard NW",
    ],
    terrain:
      "Bench-and-ravine ground: level master-planned streets off Tuscany Boulevard NW that end where Twelve Mile Coulee Natural Area cuts its ravine along the community's flank.",
    windNote:
      "Chinook belt. The coulee channels wind along its own axis, so the rim and the interior streets can read differently — both are checked before flying the edge.",
    seasonalWindow:
      "Autumn is the coulee's season: the ravine turns colour weeks before the streetscape changes, and low fall light models its depth. Summer evenings put the golden hour over the foothill horizon.",
    bylawNote:
      "City of Calgary parks bylaws prohibit drone takeoff and landing in municipal parks and natural areas, so Tuscany flights launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 25 minutes from the Calgary base — a comfortable same-day booking, and one trip can also cover Arbour Lake, Rocky Ridge or Crestmont.",
    faqs: [
      {
        question: "What airspace rules apply to drone flights over Tuscany?",
        answer:
          "Tuscany is one of the farther north-west communities from the airport — about 16 km from Calgary International, near the charted edge of the CYYC control zone. Flights are planned as controlled airspace until NAV Drone shows otherwise for the specific address, and where authorisation is required it is filed before the date is booked.",
      },
      {
        question: "Can a Tuscany listing's aerials show Twelve Mile Coulee Natural Area?",
        answer:
          "Yes. Takeoff and landing stay out of the natural area under city bylaw, but photographing it from authorised airspace over a private launch is routine. For homes along the rim, the coulee in frame is usually the strongest single argument the listing has.",
      },
    ],
    primaryKeywords: [
      "drone photography Tuscany",
      "aerial photography Tuscany",
      "Tuscany drone services",
    ],
    nearestLocations: ["arbour-lake", "rocky-ridge", "royal-oak", "crestmont"],
  },

  // ───────────────────────────────────────────────── UNIVERSITY DISTRICT ──
  {
    slug: "university-district",
    name: "University District",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.083, lng: -114.142 },
    tier: 3,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "University District lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 1 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "University District was drawn as a walkable grid from its first plan — University District Drive NW and Innovation Boulevard NW cross at city-block intervals — and from altitude that tight grid reads as a deliberate break from the curved streets around it.",
      consequence:
        "Retail and development clients here are selling the plan itself — street wall, block rhythm, build-out pace — and no ground camera can show a plan; it can only show one corner of it.",
      resolution:
        "An approach run down the boulevard, or a repeat progress series over the active blocks, puts the whole plan in frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "University District Drive NW",
      "Innovation Boulevard NW",
      "Rectangle Park",
      "University of Calgary",
      "Alberta Children's Hospital",
    ],
    terrain:
      "A level plateau parcel, compact and fully urban — the relief in an aerial frame here comes from building mass and block pattern, not from the ground.",
    windNote:
      "Chinook belt, on an open plateau. Mid-rise blocks generate local gusting and channelling at street level, so low passes between buildings get their own wind assessment.",
    seasonalWindow:
      "The flat site takes the full golden hour year-round. Winter's low sun rakes the building faces and makes the street wall legible; summer evenings favour Rectangle Park and the retail frontage.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in parks and public spaces, so University District flights stage from private or client-controlled property with consent.",
    subjectMix: ["commercial-retail", "construction-progress", "new-build-subdivision"],
    mobilisationNote:
      "About 15 minutes from the Calgary base — practical at short notice, and easily paired with University Heights, Varsity or Brentwood in a single mobilisation.",
    faqs: [
      {
        question: "What approvals does a drone shoot in University District need?",
        answer:
          "Airspace authorisation, arranged in advance. The district is about 10 km from Calgary International, inside the CYYC control zone, so flights operate under an Advanced RPAS certificate with NAV CANADA authorisation obtained before the shoot is confirmed — and plans keep clear of the Alberta Children's Hospital heliport approaches.",
      },
      {
        question: "Does the Alberta Children's Hospital heliport limit where you can fly?",
        answer:
          "It fixes part of the route. The heliport operates about 1 km away and its approaches take absolute priority, so flight plans around University District are drawn to stay clear of them. In practice that shapes the path to a frame, not whether the frame can be made.",
      },
      {
        question: "Can you document University District's build-out over time?",
        answer:
          "Yes. Repeat flights on fixed waypoints and matched altitudes record each block's rise against the grid, visit after visit, so the series stays comparable. The control-zone authorisation is arranged for every flight as part of scheduling, so the series never slips its calendar.",
      },
    ],
    primaryKeywords: [
      "drone photography University District",
      "aerial photography University District",
      "University District drone services",
    ],
    nearestLocations: ["university-heights", "varsity", "brentwood"],
  },
{
    slug: "university-heights",
    name: "University Heights",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.079, lng: -114.135 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "University Heights lies inside the charted CYYC control zone, about 10 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Alberta Children's Hospital heliport, operating about 1 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "University Heights is a small pocket of mid-century residential streets ringed by institutions — the University of Calgary, Foothills Medical Centre, Alberta Children's Hospital and McMahon Stadium — so from altitude the community reads as a residential island inside a campus.",
      consequence:
        "Proximity to those institutions is what a buyer here is paying for, and it is exactly what a ground-level photographer cannot show — from the sidewalk, an infill on Uxbridge Drive NW looks like any other suburban street in the city.",
      resolution:
        "A rising reveal lifts from the property until the campus and the hospital towers enter the top of the frame, flown under the CYYC authorisation filed at booking and delivered in the standard turnaround.",
    },
    landmarks: [
      "University of Calgary",
      "Foothills Medical Centre",
      "Alberta Children's Hospital",
      "McMahon Stadium",
      "Uxbridge Drive NW",
    ],
    terrain:
      "A level plateau community. The grid sits flat between the campus and the hospital lands, and just south of Foothills Medical Centre the ground breaks away to the river valley.",
    windNote:
      "Chinook belt. The plateau offers no meaningful shelter from a west wind, so a chinook day is read from the gust spread rather than the average — the average often looks flyable when the gusts are not.",
    seasonalWindow:
      "Snow sits evenly on the flat grid, so winter frames read clean, and long summer evenings put low light across the rooflines. The practical constraint is late-summer wildfire smoke flattening contrast.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, so launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Same-day capable once the CYYC authorisation is in hand, and it pairs naturally with University District, Varsity or Banff Trail on the same outing.",
    faqs: [
      {
        question: "Can a drone shoot go ahead this close to the Alberta Children's Hospital heliport?",
        answer:
          "Yes, with planning. University Heights sits about 10 km from Calgary International inside the CYYC control zone, so flights run under an Advanced RPAS certificate with NAV CANADA authorisation filed before the date is confirmed. The hospital heliport operates about 1 km away, and every flight plan keeps clear of its approaches.",
      },
      {
        question: "What does aerial photography show for a University Heights property that ground photos miss?",
        answer:
          "Position. The community's value sits in its walking distance to the University of Calgary, Foothills Medical Centre and the hospitals, and none of that appears in a street-level frame. From altitude the campus and the medical district frame the property, and the buyer sees exactly why the address commands its price.",
      },
      {
        question: "How quickly can a University Heights shoot be scheduled?",
        answer:
          "The drive is about 15 minutes, so the schedule is set by paperwork and weather rather than distance. Authorisation is filed as part of booking; once it is in hand, same-day flying is realistic, and the shoot can share an outing with Varsity or University District next door.",
      },
    ],
    primaryKeywords: [
      "drone photography University Heights",
      "aerial photography University Heights",
      "University Heights drone services",
    ],
    nearestLocations: ["university-district", "varsity", "banff-trail"],
  },

  {
    slug: "valley-ridge",
    name: "Valley Ridge",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.098, lng: -114.228 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 15,
      authorizationRequired: true,
      authorizationNote:
        "Valley Ridge lies inside the charted CYYC control zone, about 15 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Valley Ridge was laid out around its golf course rather than beside it — from altitude the street loops read as rings of rooflines threaded between the fairways of Valley Ridge Golf Course, with the Bow River Pathway closing the community's river edge.",
      consequence:
        "A fairway-backing lot carries its premium in that relationship, and a ground photographer shooting over the back fence reduces it to a strip of mown grass — the buyer never sees how the hole wraps the property.",
      resolution:
        "A boundary-line orbit holds the house and its fairway in the same frame without crossing over play, flown under the CYYC authorisation arranged at booking and delivered in the standard turnaround.",
    },
    landmarks: [
      "Valley Ridge Golf Course",
      "Bow River Pathway",
      "Valley Ridge Boulevard NW",
      "Valley Ridge Community Centre",
    ],
    terrain:
      "A bench-and-valley community at Calgary's western edge. The ground steps down through the golf course toward the river flats, with the escarpment behind separating Valley Ridge from the plateau above.",
    windNote:
      "Chinook belt, with the valley adding its own behaviour — west wind accelerates along the river corridor, so conditions on the flats can run rougher than the city forecast implies.",
    seasonalWindow:
      "The golf season sets the palette — from green-up until the first lasting snow the fairways carry the frame's colour, and autumn along the Bow River Pathway gives the strongest light of the year.",
    bylawNote:
      "Calgary's parks bylaws bar takeoff and landing in municipal parks and on the pathway system; launch and recovery are arranged on private property with the owner's consent.",
    subjectMix: ["luxury-residential", "new-build-subdivision"],
    mobilisationNote:
      "About 20 minutes west of the Calgary base on the Trans-Canada. Comfortable as a same-day booking, and it pairs well with Bowness, Silver Springs or Scenic Acres on a single trip west.",
    faqs: [
      {
        question: "Does Valley Ridge need airspace authorisation even at the city's western edge?",
        answer:
          "Yes. Despite the distance — about 15 km from Calgary International — Valley Ridge is charted inside the CYYC Class C control zone, so flights need an Advanced RPAS certificate and a NAV CANADA authorisation, which Fly4MEdia obtains before confirming the shoot. The specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can you photograph a home backing the golf course without flying over play?",
        answer:
          "Yes. The flight path is planned along the lot boundary rather than over the fairway, launched from the client's own property, and timed to keep well clear of players. The orbit still holds the house and the hole in one frame, because the relationship — not the grass — is the shot.",
      },
      {
        question: "How does the 20-minute drive affect booking a Valley Ridge shoot?",
        answer:
          "Very little. Twenty minutes west on the Trans-Canada keeps Valley Ridge comfortably inside same-day range once weather and the authorisation align, and it pairs naturally with Bowness, Silver Springs or Scenic Acres, so multi-property days on the city's west side are routine to arrange.",
      },
    ],
    primaryKeywords: [
      "drone photography Valley Ridge",
      "aerial photography Valley Ridge",
      "Valley Ridge drone services",
    ],
    nearestLocations: ["bowness", "silver-springs", "scenic-acres"],
  },

  {
    slug: "varsity",
    name: "Varsity",
    region: "calgary-nw",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.09, lng: -114.15 },
    tier: 2,
    driveMinutesFromCalgary: 20,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "About 10 km from Calgary International, Varsity falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Alberta Children's Hospital heliport operates about 2 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Alberta Children's Hospital heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From its own streets Varsity reads as a landlocked suburb, but past Varsity Estates Drive NW the ground falls away into the river valley — invisible at street level, unmistakable from altitude.",
      consequence:
        "Estate listings on that western edge are priced for the valley outlook, and a driveway photograph erases it — the frame shows an ordinary frontage, and the premium reads as unjustified.",
      resolution:
        "A rising reveal brings the valley over the rooftops at authorised altitude, putting the outlook the price is built on back into the listing, delivered in the standard turnaround.",
    },
    landmarks: [
      "Varsity Estates Drive NW",
      "Varsity Drive NW",
      "Market Mall",
      "Dalhousie Station",
    ],
    terrain:
      "Level plateau across most of the grid, breaking into a wooded valley slope along the community's western flank — the estates side carries almost all of the elevation change.",
    windNote:
      "Chinook country. Winter west winds arrive with a wide gust spread, and the spread is what grounds an airframe, so bookings in the chinook months carry a weather date as standard.",
    seasonalWindow:
      "Autumn is the strongest season on the western edge — the valley slope turns while the grid stays muted, and the contrast draws the eye straight to the outlook a listing there is selling.",
    bylawNote:
      "Municipal parks and pathways in Calgary are closed to drone takeoff and landing under city bylaws, so flights are launched from private property with the client's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 20 minutes from the base. Once authorisation is filed it is a straightforward same-day shoot, and Dalhousie, Brentwood or Charleswood can share the same trip northwest.",
    faqs: [
      {
        question: "What airspace rules apply to drone flights in Varsity?",
        answer:
          "Varsity is charted inside the Calgary International Class C control zone, about 10 km from the airport. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, plans keep clear of the hospital heliport approaches about 2 km away, and NAV Drone confirms each site.",
      },
      {
        question: "Why is aerial photography worth it for a Varsity Estates listing?",
        answer:
          "Because the outlook is the premium. On the community's western edge, past Varsity Estates Drive NW, the ground falls to the river valley — a relationship invisible from the street. An authorised climb to legal altitude brings the valley into frame above the roofline: the single image that justifies the asking price.",
      },
      {
        question: "Can commercial properties near Market Mall be photographed from the air?",
        answer:
          "Yes. Retail and commercial work around Market Mall runs under the same CYYC authorisation as residential flights, with launch staged from the subject property with the owner's consent. Orbits and elevated frames show parking, access and anchor position — the context a leasing brochure cannot capture from the ground.",
      },
    ],
    primaryKeywords: [
      "drone photography Varsity",
      "aerial photography Varsity",
      "Varsity drone services",
    ],
    nearestLocations: ["dalhousie", "brentwood", "charleswood"],
  }
];
